export interface AchievementPoint {
    type: AchievementType;
    value: number;
}

export type AchievementType =
    'ON_HALF_WAY'
    | 'WEIGHT_LOSS';

export class AchievementTypeValues {
    static ON_HALF_WAY: AchievementType = 'ON_HALF_WAY';
    static WEIGHT_LOSS: AchievementType = 'WEIGHT_LOSS';
}

export interface AddAdminCoupon$Request {
    id: CouponId;
    value: CouponValue;
}

export interface AddAdminCoupon$Response {
}

export interface AddCartItem$Request {
    cartId: CartId;
    items: CartItem[];
}

export interface AddCartItem$Response {
    provider: ProviderName;
    shopcartItemList: Shopcart[];
    url: string;
}

export interface AddEntitlement$Request {
    days: string;
    level: Level;
    userId: UserId;
}

export interface AddEntitlement$Response {
}

export interface AddFamilyMember$Request {
    profile: Profile;
}

export interface AddFamilyMember$Response {
    profile: Profile;
}

export interface AddMeal$Request {
    mealId: MealId;
    recipeId: RecipeId;
}

export interface AddMeal$Response {
    meal: MealSummary;
}

export interface AddOtherItem$Request {
    name: string;
}

export interface AddOtherItem$Response {
    shoplist: Shoplist;
    status: ShoplistStatus;
}

export interface AddProfileToMeal$Request {
    mealId: MealId;
    profileId: ProfileId;
}

export interface AddProfileToMeal$Response {
}

export interface AdminAllergen {
    id: AllergyId;
    names: TranslationValue[];
    published: boolean;
}

export interface AdminFullIngredient {
    allergens: AllergyId[];
    calories: number;
    carbs: number;
    fats: number;
    id: IngredientId;
    names: TranslationValue[];
    perishable: boolean;
    proteins: number;
    recommendations: TranslationValue[];
    shoplistCategory: ShoplistCategory;
    unit: IngredientUnit;
}

export interface AdminFullProduct {
    amount: number;
    calories: number;
    carbs: number;
    hasPicture: boolean;
    id: ProductId;
    imageBaseUrl: string;
    imageUpload: ImageUpload[];
    ingredients: string[];
    mealTypes: MealType[];
    needHeating: boolean;
    productAllergies: AllergyId[];
    productForAllergies: AllergyId[];
    productName: string;
    productStoreId: ProductStoreId;
    proteins: number;
    published: boolean;
    storeName: string;
}

export interface AdminFullRecipe {
    calories: number;
    carbs: number;
    cookingGuides: TranslationValue[];
    cookingTime: number;
    countries: Country[];
    featured: boolean;
    hasPicture: boolean;
    id: RecipeId;
    imageBaseUrl: string;
    imageUpload: ImageUpload[];
    ingredients: AdminRecipeIngredient[];
    mealTypes: MealType[];
    names: TranslationValue[];
    proTipContent: TranslationValue[];
    proTipHeader: TranslationValue[];
    proteins: number;
    published: boolean;
    recipeForAllergies: AllergyId[];
    recipeTags: RecipeTag[];
    wines: TranslationValue[];
}

export interface AdminFullSpecialRecipe {
    calories: number;
    carbs: number;
    cookingGuides: TranslationValue[];
    count: number;
    fats: number;
    id: SpecialRecipeId;
    ingredients: AdminRecipeIngredient[];
    names: TranslationValue[];
    proteins: number;
    published: boolean;
    unit: IngredientUnit;
}

export interface AdminIngredient {
    allergies: string[];
    calories: number;
    carbs: number;
    id: IngredientId;
    name: string;
    perishable: boolean;
    proteins: number;
    published: boolean;
    shoplistCategory: ShoplistCategory;
    unit: IngredientUnit;
}

export interface AdminMkPlan {
    altNames: TranslationValue[];
    currency: MkCurrency;
    deleted: boolean;
    descriptions: TranslationValue[];
    duration: number;
    id: MkPlanId;
    interval: MkPlanInterval;
    intervalCount: number;
    label: string;
    names: TranslationValue[];
    price: number;
    recurring: boolean;
    statementDescriptions: TranslationValue[];
}

export interface AdminProduct {
    allergies: string[];
    id: ProductId;
    mealTypes: MealType[];
    productName: string;
    published: boolean;
    storeName: string;
}

export interface AdminRecipe {
    allergies: string[];
    cookingTime: number;
    countries: Country[];
    featured: boolean;
    icon: string;
    mealTypes: MealType[];
    name: string;
    published: boolean;
    rating: number;
    recipeId: RecipeId;
    tags: RecipeTag[];
    voted: number;
}

export interface AdminRecipeIngredient {
    allergyIds: AllergyId[];
    calories: number;
    carbs: number;
    count: number;
    fats: number;
    id: number;
    name: string;
    nationalCount: number;
    order: number;
    proteins: number;
    replaceableWith: AdminRecipeReplacementIngredient[];
    shoplistCategory: ShoplistCategory;
    type: IngredientType;
    unit: IngredientUnit;
}

export interface AdminRecipeReplacementIngredient {
    id: IngredientId;
    name: string;
}

export interface AdminSpecialRecipe {
    icon: string;
    id: SpecialRecipeId;
    name: string;
    published: boolean;
    usages: number;
}

export interface AdminUserRow {
    activeUntil: string;
    birthDate: string;
    country: Country;
    email: string;
    familyId: FamilyId;
    hasActiveSubscription: boolean;
    iapCustomerId: IapCustomerId;
    lang: Lang;
    lastName: string;
    locked: boolean;
    mainProfileId: ProfileId;
    mkCustomerId: MkCustomerId;
    name: string;
    stripeCustomerId: StripeCustomerId;
    trialExpires: string;
    userId: UserId;
}

export interface Allergen {
    category: AllergyCategory;
    id: AllergyId;
    name: string;
}

export interface Allergy {
    category: AllergyCategory;
    id: AllergyId;
    name: string;
}

export type AllergyCategory =
    'FISH_AND_SEAFOOD'
    | 'MEATS'
    | 'NUTS_AND_SEEDS'
    | 'OTHER_ALLERGENS';

export class AllergyCategoryValues {
    static FISH_AND_SEAFOOD: AllergyCategory = 'FISH_AND_SEAFOOD';
    static MEATS: AllergyCategory = 'MEATS';
    static NUTS_AND_SEEDS: AllergyCategory = 'NUTS_AND_SEEDS';
    static OTHER_ALLERGENS: AllergyCategory = 'OTHER_ALLERGENS';
}

export type AllergyId = number;

export interface BMIValue {
    currentBmi: number;
    idealBmi: number;
    idealWeight: number;
    maxWeight: number;
    minWeight: number;
}

export interface CalculateBMI$Request {
    profile: Profile;
}

export interface CalculateBMI$Response {
    bmiValue: BMIValue;
}

export type CampaignId = string;

export interface CanSubscribeIap$Request {
}

export interface CanSubscribeIap$Response {
    canSubscribe: boolean;
}

export interface CancelAdminSubscription$Request {
    subscriptionId: SubscriptionId;
}

export interface CancelAdminSubscription$Response {
}

export interface CancelSubscription$Request {
    subscriptionId: MkSubscriptionId;
}

export interface CancelSubscription$Response {
    status: boolean;
}

export interface CancelSubscription2$Request {
    id: SubscriptionId;
}

export interface CancelSubscription2$Response {
}

export type CartId = number;

export interface CartItem {
    count: number;
    ingredientId: IngredientId;
}

export interface ChangeLanguage$Request {
    lang: Lang;
}

export interface ChangeLanguage$Response {
}

export interface ChangeUserLanguage$Request {
    lang: Lang;
}

export interface ChangeUserLanguage$Response {
}

export interface ChartData {
    chartPoints: ChartPoint[];
    chartStartDate: string;
    lastDietModeChange: DietModeChangeRow;
    toTarget: number;
}

export interface ChartPoint {
    achievements: AchievementPoint[];
    date: string;
    newMode: DietMode;
    weight: number;
}

export interface CheckOtherItem$Request {
    checked: boolean;
    itemId: ShoplistOtherItemId;
}

export interface CheckOtherItem$Response {
    status: IngredientCheckStatus;
}

export interface CheckShoplistIngredient$Request {
    checked: boolean;
    ingredientId: IngredientId;
    ingredientIds: IngredientId[];
    shoplistId: ShoplistId;
}

export interface CheckShoplistIngredient$Response {
}

export type Country =
    'EST'
    | 'GBR'
    | 'IND';

export class CountryValues {
    static EST: Country = 'EST';
    static GBR: Country = 'GBR';
    static IND: Country = 'IND';
}

export type CouponCode = string;

export type CouponId = string;

export interface CouponValue {
    active: boolean;
    amountOff: number;
    lastDayValid: string;
    maxRedemptions: number;
    percentOff: number;
    plans: PlanLabel[];
}

export interface CreateGiftSubscription$Request {
    authRequired: boolean;
    buyerUserEmail: string;
    cardToken: string;
    couponId: string;
    planId: StripePlanId;
    recipientUserEmail: string;
    threeDToken: string;
}

export interface CreateGiftSubscription$Response {
    clientSecret: string;
    redirectUrl: string;
    sourceId: string;
}

export interface CreateGiftTransaction$Request {
    buyerEmail: string;
    couponId: MkCouponId;
    message: string;
    paymentMethod: MkPaymentMethodId;
    planId: MkPlanId;
    recipientEmail: string;
    recipientEmailScheduledDate: string;
}

export interface CreateGiftTransaction$Response {
    amount: number;
    buyerEmail: string;
    currency: MkCurrency;
    transactionId: MkTransactionId;
    url: string;
}

export interface CreateMkRefund$Request {
    amount: number;
    comment: string;
    fullRefund: boolean;
    transactionId: MkTransactionId;
}

export interface CreateMkRefund$Response {
}

export interface CreateProfile$Request {
    channel: string;
    lang: Lang;
    measurementSystem: MeasurementSystem;
    profile: Profile;
}

export interface CreateProfile$Response {
    dailyMenu: DailyMenu;
    mealsAdditionalInfo: DailyMealInfo[];
    profile: Profile;
    totalCalories: number;
}

export interface CreateSelfGiftTransactions$Request {
    campaign: string;
    couponId: MkCouponId;
    email: string;
    meta: { [key: string ]: any;};
    paymentMethod: MkPaymentMethodId;
    planId: MkPlanId;
}

export interface CreateSelfGiftTransactions$Response {
    amount: number;
    currency: MkCurrency;
    email: string;
    transactionId: MkTransactionId;
    url: string;
}

export interface CreateShopcart$Request {
    provider: ProviderName;
    shoplistId: ShoplistId;
}

export interface CreateShopcart$Response {
    cartId: CartId;
}

export interface CreateShoplist$Request {
    dates: string[];
}

export interface CreateShoplist$Response {
}

export interface CreateStripePurchase$Request {
    couponCode: CouponCode;
    planLabel: PlanLabel;
    token: string;
}

export interface CreateStripePurchase$Response {
    subscriptionId: SubscriptionId;
}

export interface CreateSubscription$Request {
    authRequired: boolean;
    cardToken: string;
    couponId: string;
    planId: StripePlanId;
    threeDToken: string;
}

export interface CreateSubscription$Response {
    clientSecret: string;
    redirectUrl: string;
    sourceId: string;
}

export interface CreateTransaction$Request {
    couponId: MkCouponId;
    paymentMethod: MkPaymentMethodId;
    planId: MkPlanId;
}

export interface CreateTransaction$Response {
    amount: number;
    currency: MkCurrency;
    email: string;
    transactionId: MkTransactionId;
    url: string;
}

export type Currency =
    'EUR'
    | 'GBP'
    | 'INR';

export class CurrencyValues {
    static EUR: Currency = 'EUR';
    static GBP: Currency = 'GBP';
    static INR: Currency = 'INR';
}

export interface DailyMealInfo {
    mealId: MealId;
    mealType: MealType;
    noAllergies: string[];
    recipesCount: number;
}

export interface DailyMenu {
    date: string;
    meals: MealSummary[];
}

export type DayOfWeek =
    'FRIDAY'
    | 'MONDAY'
    | 'SATURDAY'
    | 'SUNDAY'
    | 'THURSDAY'
    | 'TUESDAY'
    | 'WEDNESDAY';

export class DayOfWeekValues {
    static FRIDAY: DayOfWeek = 'FRIDAY';
    static MONDAY: DayOfWeek = 'MONDAY';
    static SATURDAY: DayOfWeek = 'SATURDAY';
    static SUNDAY: DayOfWeek = 'SUNDAY';
    static THURSDAY: DayOfWeek = 'THURSDAY';
    static TUESDAY: DayOfWeek = 'TUESDAY';
    static WEDNESDAY: DayOfWeek = 'WEDNESDAY';
}

export interface DeactivateShoplist$Request {
}

export interface DeactivateShoplist$Response {
}

export type DietMode =
    'WEIGHT_GAIN'
    | 'WEIGHT_LOSS'
    | 'WEIGHT_MAINTENANCE';

export class DietModeValues {
    static WEIGHT_GAIN: DietMode = 'WEIGHT_GAIN';
    static WEIGHT_LOSS: DietMode = 'WEIGHT_LOSS';
    static WEIGHT_MAINTENANCE: DietMode = 'WEIGHT_MAINTENANCE';
}

export interface DietModeChangeRow {
    currentWeight: number;
    date: string;
    mode: DietMode;
    profileId: ProfileId;
    targetWeight: number;
}

export type DietStrategy =
    'DEFAULT'
    | 'NATIONAL';

export class DietStrategyValues {
    static DEFAULT: DietStrategy = 'DEFAULT';
    static NATIONAL: DietStrategy = 'NATIONAL';
}

export interface Discount {
    amountOff: number;
    couponId: string;
    percentOff: number;
    subtotal: number;
}

export interface DonationDto {
    amount: string;
    name: string;
}

export interface EditAdminCoupon$Request {
    id: CouponId;
    value: CouponValue;
}

export interface EditAdminCoupon$Response {
}

export type EntitlementType =
    'GIFT'
    | 'IAP_PAYMENT'
    | 'MANUAL'
    | 'PAYMENT'
    | 'RETRY_GRACE'
    | 'SYSTEM'
    | 'TRIAL';

export class EntitlementTypeValues {
    static GIFT: EntitlementType = 'GIFT';
    static IAP_PAYMENT: EntitlementType = 'IAP_PAYMENT';
    static MANUAL: EntitlementType = 'MANUAL';
    static PAYMENT: EntitlementType = 'PAYMENT';
    static RETRY_GRACE: EntitlementType = 'RETRY_GRACE';
    static SYSTEM: EntitlementType = 'SYSTEM';
    static TRIAL: EntitlementType = 'TRIAL';
}

export type ExerciseLevel =
    'ACTIVE'
    | 'LOW'
    | 'MODERATE'
    | 'SEDENTARY'
    | 'VERY_ACTIVE';

export class ExerciseLevelValues {
    static ACTIVE: ExerciseLevel = 'ACTIVE';
    static LOW: ExerciseLevel = 'LOW';
    static MODERATE: ExerciseLevel = 'MODERATE';
    static SEDENTARY: ExerciseLevel = 'SEDENTARY';
    static VERY_ACTIVE: ExerciseLevel = 'VERY_ACTIVE';
}

export type FamilyId = number;

export type Feature =
    'PREPARED_AND_EATING_OUT'
    | 'SHOPCART_FOODMOOD'
    | 'WEB_CONTENT'
    | 'WEB_EXPERT_CONTENT'
    | 'WEB_GIFT_CONTENT'
    | 'WEB_TRAINING_CONTENT'
    | 'WINES';

export class FeatureValues {
    static PREPARED_AND_EATING_OUT: Feature = 'PREPARED_AND_EATING_OUT';
    static SHOPCART_FOODMOOD: Feature = 'SHOPCART_FOODMOOD';
    static WEB_CONTENT: Feature = 'WEB_CONTENT';
    static WEB_EXPERT_CONTENT: Feature = 'WEB_EXPERT_CONTENT';
    static WEB_GIFT_CONTENT: Feature = 'WEB_GIFT_CONTENT';
    static WEB_TRAINING_CONTENT: Feature = 'WEB_TRAINING_CONTENT';
    static WINES: Feature = 'WINES';
}

export interface FullMealRecipe {
    calories: number;
    carbs: number;
    cookingGuide: string;
    cookingTime: number;
    date: string;
    description: string;
    hasPicture: boolean;
    id: RecipeId;
    imageBaseUrl: string;
    ingredients: MealIngredient[];
    mealId: MealId;
    mealType: MealType;
    name: string;
    priceCategory: number;
    proTipFooter: string;
    proTipHeader: string;
    rating: number;
    recipeTags: RecipeTag[];
    specialRecipes: RecipeSpecialIngredient[];
    wines: MealIngredient[];
}

export interface GenerateMenu$Request {
    userId: UserId;
}

export interface GenerateMenu$Response {
}

export interface GenerateUserMagicLink$Request {
    userId: UserId;
}

export interface GenerateUserMagicLink$Response {
    magicLink: string;
}

export interface GetAdminAllergen$Request {
    id: AllergyId;
}

export interface GetAdminAllergen$Response {
    allergen: AdminAllergen;
}

export interface GetAdminAllergenList$Request {
}

export interface GetAdminAllergenList$Response {
    allergens: Allergen[];
}

export interface GetAdminAllergens$Request {
}

export interface GetAdminAllergens$Response {
    allergens: AdminAllergen[];
}

export interface GetAdminCoupon$Request {
    couponId: MkCouponId;
}

export interface GetAdminCoupon$Response {
    coupon: MkCouponRow;
}

export interface GetAdminCoupon2$Redemption {
    count: number;
    plan: PlanLabel;
}

export interface GetAdminCoupon2$Request {
    id: CouponId;
}

export interface GetAdminCoupon2$Response {
    redemptions: GetAdminCoupon2$Redemption[];
    value: CouponValue;
}

export interface GetAdminCoupons$Request {
    couponId: MkCouponId;
}

export interface GetAdminCoupons$Response {
    coupons: MkCouponRow[];
}

export interface GetAdminIngredient$Request {
    ingredientId: IngredientId;
}

export interface GetAdminIngredient$Response {
    ingredient: AdminFullIngredient;
}

export interface GetAdminIngredients$Request {
}

export interface GetAdminIngredients$Response {
    ingredients: AdminIngredient[];
}

export interface GetAdminLegalTexts$Request {
}

export interface GetAdminLegalTexts$Response {
    legalTexts: LegalTextRow[];
}

export interface GetAdminMkExtendedSubscription$Request {
    subscriptionId: MkSubscriptionId;
}

export interface GetAdminMkExtendedSubscription$Response {
    subscription: MkAdminExtendedSubscription;
}

export interface GetAdminMkTransactions$Request {
    subscriptionId: MkSubscriptionId;
}

export interface GetAdminMkTransactions$Response {
    transactions: MkTransactionPaymentRow[];
}

export interface GetAdminPlan$Request {
    id: MkPlanId;
}

export interface GetAdminPlan$Response {
    plan: AdminMkPlan;
}

export interface GetAdminPlanList$PlanSummary {
    description: string;
    label: PlanLabel;
}

export interface GetAdminPlanList$Request {
}

export interface GetAdminPlanList$Response {
    plans: GetAdminPlanList$PlanSummary[];
}

export interface GetAdminPlans$Request {
}

export interface GetAdminPlans$Response {
    plans: AdminMkPlan[];
}

export interface GetAdminProduct$Request {
    productId: ProductId;
}

export interface GetAdminProduct$Response {
    allergens: Allergen[];
    product: AdminFullProduct;
    productStores: ProductStoreRow[];
}

export interface GetAdminProducts$Request {
}

export interface GetAdminProducts$Response {
    allergens: Allergen[];
    productStores: ProductStoreRow[];
    products: AdminProduct[];
}

export interface GetAdminRecipe$Request {
    recipeId: RecipeId;
}

export interface GetAdminRecipe$Response {
    allergens: Allergen[];
    recipe: AdminFullRecipe;
}

export interface GetAdminRecipes$Request {
}

export interface GetAdminRecipes$Response {
    recipes: AdminRecipe[];
}

export interface GetAdminSpecialRecipe$Request {
    specialRecipeId: SpecialRecipeId;
}

export interface GetAdminSpecialRecipe$Response {
    recipe: AdminFullSpecialRecipe;
}

export interface GetAdminSpecialRecipes$Request {
}

export interface GetAdminSpecialRecipes$Response {
    specialRecipes: AdminSpecialRecipe[];
}

export interface GetAdminSubscriptionDetails$EntitlementDto {
    firstDay: string;
    lastDay: string;
    paymentLink: string;
    referenceId: string;
    type: EntitlementType;
}

export interface GetAdminSubscriptionDetails$QueueDto {
    createdAt: number;
    paymentLink: string;
    period: string;
    referenceId: string;
    type: EntitlementType;
}

export interface GetAdminSubscriptionDetails$Request {
    userId: UserId;
}

export interface GetAdminSubscriptionDetails$Response {
    entitlements: GetAdminSubscriptionDetails$EntitlementDto[];
    queue: GetAdminSubscriptionDetails$QueueDto[];
    subscriptions: GetAdminSubscriptionDetails$SubscriptionDto[];
}

export interface GetAdminSubscriptionDetails$SubscriptionDto {
    active: boolean;
    amount: number;
    couponId: CouponId;
    createdAt: number;
    currency: string;
    description: string;
    failedPayments: number;
    id: SubscriptionId;
    paymentEntitlement: number;
    planLabel: PlanLabel;
    plannedPayments: number;
    provider: PaymentProvider;
    successfulPayments: number;
    token: string;
}

export interface GetAdminUser$Request {
    userId: UserId;
}

export interface GetAdminUser$Response {
    user: AdminUserRow;
}

export interface GetAdminUsers$Request {
}

export interface GetAdminUsers$Response {
    adminUsers: UserRow[];
}

export interface GetAllRecipes$Item {
    imageUrl: string;
    name: string;
}

export interface GetAllRecipes$Request {
}

export interface GetAllRecipes$Response {
    breakfast: GetAllRecipes$Item[];
    dinner: GetAllRecipes$Item[];
    lunch: GetAllRecipes$Item[];
    snack: GetAllRecipes$Item[];
}

export interface GetAllUsers$Request {
    country: Country;
    email: string;
    name: string;
    userId: UserId;
}

export interface GetAllUsers$Response {
    users: AdminUserRow[];
}

export interface GetAllergens$Request {
    lang: Lang;
}

export interface GetAllergens$Response {
    allergens: Allergy[];
}

export interface GetAveragePrices$Request {
    mealId: MealId;
}

export interface GetAveragePrices$Response {
    cat1: number;
    cat2: number;
    cat3: number;
}

export interface GetAveragePricesPerDay$Request {
}

export interface GetAveragePricesPerDay$Response {
    cat1: number;
    cat2: number;
    cat3: number;
}

export interface GetCartId$Request {
    shoplistId: ShoplistId;
}

export interface GetCartId$Response {
    cartId: CartId;
}

export interface GetCharityCampaignDetails$Request {
    campaignId: CampaignId;
}

export interface GetCharityCampaignDetails$Response {
    allDonations: DonationDto[];
    allNames: string[];
}

export interface GetCharityCampaignSummary$Request {
    campaignId: CampaignId;
}

export interface GetCharityCampaignSummary$Response {
    joinedCount: number;
    latestDonations: DonationDto[];
    latestNames: string[];
    totalAmount: number;
    totalAmountString: string;
}

export interface GetDailyMenu$Request {
    date: string;
    direction: number;
    getNextIfNotFound: boolean;
}

export interface GetDailyMenu$Response {
    dailyMenu: DailyMenu;
}

export interface GetDashboardNotifications$Request {
}

export interface GetDashboardNotifications$Response {
    daysSinceLastWeightUpdate: number;
    daysTillSubscriptionExp: number;
    daysTillTrialExp: number;
}

export type GetDiscounts$CouponStatus =
    'NOT_FOUND'
    | 'NOT_VALID'
    | 'OK';

export class GetDiscounts$CouponStatusValues {
    static NOT_FOUND: GetDiscounts$CouponStatus = 'NOT_FOUND';
    static NOT_VALID: GetDiscounts$CouponStatus = 'NOT_VALID';
    static OK: GetDiscounts$CouponStatus = 'OK';
}

export interface GetDiscounts$Discount {
    amountOff: number;
    newAmount: number;
    oldAmount: number;
    percentOff: number;
    planLabel: PlanLabel;
}

export interface GetDiscounts$Request {
    couponId: CouponId;
}

export interface GetDiscounts$Response {
    discounts: GetDiscounts$Discount[];
    status: GetDiscounts$CouponStatus;
}

export interface GetExtendedChartPoints$Request {
    endDate: string;
    profileId: ProfileId;
    startDate: string;
}

export interface GetExtendedChartPoints$Response {
    chartData: ChartData;
}

export interface GetFamilyAllergens$Request {
}

export interface GetFamilyAllergens$Response {
    allergens: Allergen[];
}

export interface GetFamilyMemberList$Request {
}

export interface GetFamilyMemberList$Response {
    familyRows: Profile[];
}

export interface GetGiftPlans$Request {
}

export interface GetGiftPlans$Response {
    plans: SubscriptionPlan[];
}

export interface GetIapPlans$Request {
}

export interface GetIapPlans$Response {
    plans: IapPlan[];
}

export interface GetIngredientReplacements$Request {
    ingredientId: IngredientId;
    recipeId: RecipeId;
}

export interface GetIngredientReplacements$Response {
    ingredients: Ingredient[];
}

export interface GetKpiDates$Request {
}

export interface GetKpiDates$Response {
    kpiGroups: KpiGroup[];
    kpis: Kpi[];
}

export interface GetLegalTexts$Request {
    lang: Lang;
}

export interface GetLegalTexts$Response {
    cookiePolicy: string;
    privacyPolicy: string;
    termsOfUse: string;
}

export interface GetMeal$Request {
    mealId: MealId;
}

export interface GetMeal$Response {
    meal: MealSummary;
}

export interface GetMealProfiles$Request {
    mealId: MealId;
}

export interface GetMealProfiles$Response {
    mealProfiles: MealProfile[];
    totalCalories: number;
}

export interface GetMealRecipe$Request {
    mealId: MealId;
}

export interface GetMealRecipe$Response {
    mealProfiles: MealProfile[];
    mealRecipe: FullMealRecipe;
    protips: Protip[];
}

export interface GetMkCouponUses$Request {
    couponId: MkCouponId;
}

export interface GetMkCouponUses$Response {
    emails: string[];
}

export interface GetMkGiftPlans$Request {
    couponId: MkCouponId;
}

export interface GetMkGiftPlans$Response {
    plans: MkPlan[];
}

export interface GetMkLastPayments$Request {
}

export interface GetMkLastPayments$Response {
    lastPayments: MkTransactionPayment[];
}

export interface GetMkPaymentHistory$Request {
}

export interface GetMkPaymentHistory$Response {
    payments: MkPaymentHistory[];
}

export interface GetMkPlans$Request {
    couponId: MkCouponId;
}

export interface GetMkPlans$Response {
    plans: MkPlan[];
}

export interface GetMkSubscriptions$Request {
    activeOnly: boolean;
    email: string;
}

export interface GetMkSubscriptions$Response {
    subscriptions: MkAdminSubscription[];
}

export interface GetPaymentHistory$Request {
}

export interface GetPaymentHistory$Response {
    paymentHistoryList: PaymentHistory[];
}

export interface GetPaymentNotification$Request {
}

export interface GetPaymentNotification$Response {
    cardData: SourceData;
    numberOfFailedPayments: number;
}

export interface GetPaymentStatus$Entitlement {
    firstDay: string;
    lastDay: string;
    type: EntitlementType;
}

export interface GetPaymentStatus$Request {
}

export interface GetPaymentStatus$Response {
    entitlements: GetPaymentStatus$Entitlement[];
    subscriptions: GetPaymentStatus$Subscription[];
}

export interface GetPaymentStatus$Subscription {
    active: boolean;
    amount: number;
    cancellable: boolean;
    description: string;
    id: SubscriptionId;
    nextPaymentDate: string;
    planLabel: PlanLabel;
    provider: PaymentProvider;
    remainingPayments: number;
}

export interface GetPdfGenerationToken$Request {
}

export interface GetPdfGenerationToken$Response {
    token: string;
}

export interface GetPlans$Config {
    currency: Currency;
    mkKey: string;
    paymentProvider: PaymentProvider;
    stripeKey: string;
}

export interface GetPlans$Plan {
    amount: number;
    label: PlanLabel;
    level: Level;
    months: number;
    recurring: boolean;
    subscription: boolean;
}

export interface GetPlans$Request {
    withLevel: boolean;
}

export interface GetPlans$Response {
    config: GetPlans$Config;
    plans: GetPlans$Plan[];
}

export interface GetPreferences$Request {
}

export interface GetPreferences$Response {
    preferences: PreferencesDto;
}

export interface GetProductStores$Request {
}

export interface GetProductStores$Response {
    stores: ProductStoreRow[];
}

export interface GetProducts$Request {
    mealId: MealId;
    mealType: MealType;
}

export interface GetProducts$Response {
    calories: number;
    storeProducts: StoreProducts[];
}

export interface GetProfile$Request {
}

export interface GetProfile$Response {
    isPasswordSet: boolean;
    measurementSystem: MeasurementSystem;
    profile: Profile;
    showRecipeCarbs: boolean;
}

export interface GetRandomRecipes$Request {
    mealId: MealId;
    recipeIds: RecipeId[];
}

export interface GetRandomRecipes$Response {
    recipeIds: RecipeId[];
    recipes: RecipeSearchItem[];
}

export interface GetRecipes$Request {
    mealId: MealId;
}

export interface GetRecipes$Response {
    ingredients: IngredientListItem[];
    recipes: RecipeSearchItem[];
}

export interface GetRecipesOld$Request {
    mealType: MealType;
}

export interface GetRecipesOld$Response {
    recipes: MealRecipe[];
}

export interface GetReplaceableIngredients$Request {
}

export interface GetReplaceableIngredients$Response {
    ingredientReplacements: IngredientReplacement[];
}

export interface GetShopcart$Request {
    cartId: CartId;
}

export interface GetShopcart$Response {
    provider: ProviderName;
    shopcartItemList: Shopcart[];
    submittedAt: number;
    url: string;
}

export interface GetShoplist$Request {
}

export interface GetShoplist$Response {
    shoplist: Shoplist;
    status: ShoplistStatus;
}

export interface GetShoplistOptions$Request {
}

export interface GetShoplistOptions$Response {
    options: ShoplistOption[];
}

export interface GetSubscriptionData$Request {
}

export interface GetSubscriptionData$Response {
    hasActivePayment: boolean;
    hasActiveTrial: boolean;
    hasTrialUntil: string;
    iapCustomerId: IapCustomerId;
    iapSubscriptionData: IapSubscriptionData;
    mkCustomerId: MkCustomerId;
    mkSubscriptionData: MkSubscriptionData[];
    paidUntil: string;
    stripeCustomerId: StripeCustomerId;
    stripeSubscriptionData: SubscriptionData[];
}

export interface GetSubscriptionPlans$Request {
}

export interface GetSubscriptionPlans$Response {
    plans: SubscriptionPlan[];
}

export interface GetSubscriptionStatus$NextPayment {
    amount: number;
    date: string;
}

export interface GetSubscriptionStatus$Request {
}

export interface GetSubscriptionStatus$Response {
    current: GetSubscriptionStatus$State;
    upcoming: GetSubscriptionStatus$State;
}

export interface GetSubscriptionStatus$State {
    nextPayment: GetSubscriptionStatus$NextPayment;
    status: GetSubscriptionStatus$Status;
    subscriptionId: SubscriptionId;
    validUntil: string;
}

export type GetSubscriptionStatus$Status =
    'FIXED'
    | 'NONE'
    | 'TERMINAL_RECURRING'
    | 'TERMLESS_RECURRING'
    | 'TRIAL';

export class GetSubscriptionStatus$StatusValues {
    static FIXED: GetSubscriptionStatus$Status = 'FIXED';
    static NONE: GetSubscriptionStatus$Status = 'NONE';
    static TERMINAL_RECURRING: GetSubscriptionStatus$Status = 'TERMINAL_RECURRING';
    static TERMLESS_RECURRING: GetSubscriptionStatus$Status = 'TERMLESS_RECURRING';
    static TRIAL: GetSubscriptionStatus$Status = 'TRIAL';
}

export interface GetThumbnailChartPoints$Request {
    profileId: ProfileId;
}

export interface GetThumbnailChartPoints$Response {
    chartData: ChartData;
}

export interface GetTransactionStatus$Request {
    transactionId: MkTransactionId;
}

export interface GetTransactionStatus$Response {
    purchaseId: PurchaseId;
    status: MkTransactionState;
}

export interface GetUserLocalDate$Request {
}

export interface GetUserLocalDate$Response {
    localDate: string;
}

export interface GetWeekMenu$Request {
    date: string;
}

export interface GetWeekMenu$Response {
    weekMenu: DailyMenu[];
}

export interface HandleMkReturnMessage$Request {
    json: string;
    mac: string;
}

export interface HandleMkReturnMessage$Response {
    purchaseId: PurchaseId;
}

export type IapCustomerId = number;

export interface IapPlan {
    altName: string;
    currency: Currency;
    interval: IapPlanInterval;
    intervalCount: number;
    key: string;
    name: string;
    planId: IapPlanId;
    price: number;
    recurring: boolean;
    shortDescription: string;
    statementDescriptor: string;
}

export type IapPlanId = number;

export type IapPlanInterval =
    'MONTH'
    | 'WEEK'
    | 'YEAR';

export class IapPlanIntervalValues {
    static MONTH: IapPlanInterval = 'MONTH';
    static WEEK: IapPlanInterval = 'WEEK';
    static YEAR: IapPlanInterval = 'YEAR';
}

export interface IapSubscriptionData {
    createdAt: number;
    currency: Currency;
    customerId: IapCustomerId;
    endDate: string;
    intervalCount: number;
    planId: IapPlanId;
    planInterval: IapPlanInterval;
    planKey: string;
    planName: string;
    planStatementDescriptor: string;
    price: number;
    recurring: boolean;
}

export type ImageSize =
    'IMAGE_ORIGINAL'
    | 'IMAGE_SQUARE';

export class ImageSizeValues {
    static IMAGE_ORIGINAL: ImageSize = 'IMAGE_ORIGINAL';
    static IMAGE_SQUARE: ImageSize = 'IMAGE_SQUARE';
}

export interface ImageUpload {
    path: string;
    type: ImageSize;
}

export interface Ingredient {
    calories: number;
    carbs: number;
    count: number;
    hidden: boolean;
    id: IngredientId;
    isReplaceable: boolean;
    name: string;
    perishable: boolean;
    proteins: number;
    recommendation: string;
    replacedWith: IngredientId;
    replacedWithName: string;
    shoplistCategory: ShoplistCategory;
    unit: IngredientUnit;
}

export type IngredientCheckStatus =
    'ALREADY_CHECKED'
    | 'ALREADY_UNCHECKED'
    | 'CHECKED'
    | 'ILLEGAL_STATE'
    | 'UNCHECKED';

export class IngredientCheckStatusValues {
    static ALREADY_CHECKED: IngredientCheckStatus = 'ALREADY_CHECKED';
    static ALREADY_UNCHECKED: IngredientCheckStatus = 'ALREADY_UNCHECKED';
    static CHECKED: IngredientCheckStatus = 'CHECKED';
    static ILLEGAL_STATE: IngredientCheckStatus = 'ILLEGAL_STATE';
    static UNCHECKED: IngredientCheckStatus = 'UNCHECKED';
}

export type IngredientId = number;

export interface IngredientListItem {
    ingredientId: IngredientId;
    name: string;
}

export interface IngredientReplacement {
    ingredient: Ingredient;
    ingredientId: IngredientId;
    replaceableWith: Ingredient[];
}

export type IngredientType =
    'INGREDIENT'
    | 'SPECIAL_INGREDIENT';

export class IngredientTypeValues {
    static INGREDIENT: IngredientType = 'INGREDIENT';
    static SPECIAL_INGREDIENT: IngredientType = 'SPECIAL_INGREDIENT';
}

export type IngredientUnit =
    'G'
    | 'HANDFUL'
    | 'ML'
    | 'PCS'
    | 'TASTE'
    | 'TBSP'
    | 'TSP';

export class IngredientUnitValues {
    static G: IngredientUnit = 'G';
    static HANDFUL: IngredientUnit = 'HANDFUL';
    static ML: IngredientUnit = 'ML';
    static PCS: IngredientUnit = 'PCS';
    static TASTE: IngredientUnit = 'TASTE';
    static TBSP: IngredientUnit = 'TBSP';
    static TSP: IngredientUnit = 'TSP';
}

export interface IsoChronology {
    calendarType: string;
    id: string;
}

export type IsoEra =
    'BCE'
    | 'CE';

export class IsoEraValues {
    static BCE: IsoEra = 'BCE';
    static CE: IsoEra = 'CE';
}

export interface Kpi {
    endDate: string;
    startDate: string;
    week: number;
}

export type KpiGroup =
    'EST_SUBSCRIBED_USER_AVERAGE'
    | 'EST_SUBSCRIPTION_OVERALL'
    | 'EST_SUBSCRIPTION_TODAY'
    | 'EST_TRIALS'
    | 'GBR_SUBSCRIBED_USER_AVERAGE'
    | 'GBR_SUBSCRIPTION_OVERALL'
    | 'GBR_SUBSCRIPTION_TODAY'
    | 'GBR_TRIALS';

export class KpiGroupValues {
    static EST_SUBSCRIBED_USER_AVERAGE: KpiGroup = 'EST_SUBSCRIBED_USER_AVERAGE';
    static EST_SUBSCRIPTION_OVERALL: KpiGroup = 'EST_SUBSCRIPTION_OVERALL';
    static EST_SUBSCRIPTION_TODAY: KpiGroup = 'EST_SUBSCRIPTION_TODAY';
    static EST_TRIALS: KpiGroup = 'EST_TRIALS';
    static GBR_SUBSCRIBED_USER_AVERAGE: KpiGroup = 'GBR_SUBSCRIBED_USER_AVERAGE';
    static GBR_SUBSCRIPTION_OVERALL: KpiGroup = 'GBR_SUBSCRIPTION_OVERALL';
    static GBR_SUBSCRIPTION_TODAY: KpiGroup = 'GBR_SUBSCRIPTION_TODAY';
    static GBR_TRIALS: KpiGroup = 'GBR_TRIALS';
}

export type Lang =
    'EN'
    | 'ET'
    | 'HI'
    | 'RU';

export class LangValues {
    static EN: Lang = 'EN';
    static ET: Lang = 'ET';
    static HI: Lang = 'HI';
    static RU: Lang = 'RU';
}

export type LegalTextId = number;

export interface LegalTextRow {
    content: string;
    country: Country;
    id: LegalTextId;
    lang: Lang;
    type: LegalTextType;
}

export type LegalTextType =
    'COOKIE_POLICY'
    | 'PRIVACY_POLICY'
    | 'TERMS_OF_USE';

export class LegalTextTypeValues {
    static COOKIE_POLICY: LegalTextType = 'COOKIE_POLICY';
    static PRIVACY_POLICY: LegalTextType = 'PRIVACY_POLICY';
    static TERMS_OF_USE: LegalTextType = 'TERMS_OF_USE';
}

export type Level =
    'BETA_TESTER'
    | 'CLASSIC'
    | 'GOLD'
    | 'NONE'
    | 'PLATINUM'
    | 'SILVER'
    | 'TRIAL';

export class LevelValues {
    static BETA_TESTER: Level = 'BETA_TESTER';
    static CLASSIC: Level = 'CLASSIC';
    static GOLD: Level = 'GOLD';
    static NONE: Level = 'NONE';
    static PLATINUM: Level = 'PLATINUM';
    static SILVER: Level = 'SILVER';
    static TRIAL: Level = 'TRIAL';
}

export interface ListAdminCoupon$CouponSummary {
    active: boolean;
    amountOff: number;
    id: CouponId;
    lastDayValid: string;
    maxRedemptions: number;
    percentOff: number;
    redemptions: number;
}

export interface ListAdminCoupon$Request {
}

export interface ListAdminCoupon$Response {
    coupons: ListAdminCoupon$CouponSummary[];
}

export interface LocalDate {
    chronology: IsoChronology;
    dayOfMonth: number;
    dayOfWeek: DayOfWeek;
    dayOfYear: number;
    era: IsoEra;
    leapYear: boolean;
    month: Month;
    monthValue: number;
    year: number;
}

export type LoginMethod =
    'FACEBOOK'
    | 'GOOGLE'
    | 'MAGIC_LINK'
    | 'PASSWORD';

export class LoginMethodValues {
    static FACEBOOK: LoginMethod = 'FACEBOOK';
    static GOOGLE: LoginMethod = 'GOOGLE';
    static MAGIC_LINK: LoginMethod = 'MAGIC_LINK';
    static PASSWORD: LoginMethod = 'PASSWORD';
}

export interface Logout$Request {
}

export interface Logout$Response {
}

export interface MarkTransactionAsPaid$Request {
    transactionId: MkTransactionId;
}

export interface MarkTransactionAsPaid$Response {
}

export type MealId = number;

export interface MealIngredient {
    count: number;
    id: IngredientId;
    isReplaceable: boolean;
    name: string;
    replacedWith: IngredientId;
    replacedWithName: string;
    unit: IngredientUnit;
}

export interface MealProfile {
    caloriesRequired: number;
    carbs: number;
    eats: boolean;
    lastName: string;
    name: string;
    percentage: number;
    profileId: ProfileId;
}

export interface MealRecipe {
    calories: number;
    carbs: number;
    cookingTime: number;
    fixPrice: number;
    hasPicture: boolean;
    id: RecipeId;
    imageBaseUrl: string;
    mealTypes: MealType[];
    proteins: number;
    rating: number;
    recipeName: string;
    varPrice: number;
}

export interface MealSummary {
    carbs: number;
    cookingTime: number;
    date: string;
    eatProduct: boolean;
    hasActiveRecipe: boolean;
    hasPicture: boolean;
    imageBaseUrl: string;
    mealId: MealId;
    mealType: MealType;
    priceCategory: number;
    rating: number;
    recipeId: RecipeId;
    recipeName: string;
    recipeTags: RecipeTag[];
    totalCalories: number;
}

export type MealType =
    'BREAKFAST'
    | 'DINNER'
    | 'LUNCH'
    | 'SNACK';

export class MealTypeValues {
    static BREAKFAST: MealType = 'BREAKFAST';
    static DINNER: MealType = 'DINNER';
    static LUNCH: MealType = 'LUNCH';
    static SNACK: MealType = 'SNACK';
}

export type MeasurementSystem =
    'IMPERIAL'
    | 'METRIC';

export class MeasurementSystemValues {
    static IMPERIAL: MeasurementSystem = 'IMPERIAL';
    static METRIC: MeasurementSystem = 'METRIC';
}

export interface MkAdminExtendedSubscription {
    active: boolean;
    cancelAtPeriodEnd: boolean;
    coupon: MkCouponId;
    created: number;
    currency: MkCurrency;
    currentPeriodEnd: string;
    currentPeriodStart: string;
    customPrice: number;
    customerId: MkCustomerId;
    email: string;
    endsOn: string;
    giftFrom: string;
    id: MkSubscriptionId;
    interval: MkPlanInterval;
    intervalCount: number;
    name: string;
    paymentsFailed: number;
    planName: string;
    planPrice: number;
    recurring: boolean;
    sourceType: MkSourceType;
    userId: UserId;
}

export interface MkAdminSubscription {
    amount: number;
    currency: MkCurrency;
    currentPeriodEnd: string;
    currentPeriodStart: string;
    customerId: MkCustomerId;
    email: string;
    interval: MkPlanInterval;
    intervalCount: number;
    name: string;
    paymentTime: number;
    planName: string;
    recurring: boolean;
    sourceType: MkSourceType;
    subscriptionId: MkSubscriptionId;
}

export type MkAdminSubscriptionAction =
    'CANCEL_AT_PERIOD_END'
    | 'END_SUBSCRIPTION';

export class MkAdminSubscriptionActionValues {
    static CANCEL_AT_PERIOD_END: MkAdminSubscriptionAction = 'CANCEL_AT_PERIOD_END';
    static END_SUBSCRIPTION: MkAdminSubscriptionAction = 'END_SUBSCRIPTION';
}

export type MkCouponDuration =
    'INFINITE_TIMES'
    | 'MULTI_MONTH'
    | 'ONCE';

export class MkCouponDurationValues {
    static INFINITE_TIMES: MkCouponDuration = 'INFINITE_TIMES';
    static MULTI_MONTH: MkCouponDuration = 'MULTI_MONTH';
    static ONCE: MkCouponDuration = 'ONCE';
}

export type MkCouponId = string;

export interface MkCouponRow {
    active: boolean;
    amountOff: number;
    createdAt: number;
    currency: MkCurrency;
    duration: MkCouponDuration;
    durationMonths: number;
    id: MkCouponId;
    maxRedemptions: number;
    percentageOff: number;
    plans: MkPlanId[];
    redeemBy: string;
    redemptions: number;
}

export type MkCurrency =
    'EUR'
    | 'GBP'
    | 'USD';

export class MkCurrencyValues {
    static EUR: MkCurrency = 'EUR';
    static GBP: MkCurrency = 'GBP';
    static USD: MkCurrency = 'USD';
}

export type MkCustomerId = number;

export type MkGiftId = string;

export interface MkPaymentHistory {
    amount: number;
    cardBrand: string;
    cardLastTwo: string;
    currency: MkCurrency;
    date: string;
    discount: Discount;
    name: string;
    periodEnd: string;
    periodStart: string;
    refund: MkRefund;
    statementDescriptor: string;
}

export interface MkPaymentMethod {
    linkUrl: string;
    logoUrl: string;
    name: string;
}

export type MkPaymentMethodId = string;

export interface MkPaymentMethodObject {
    id: MkPaymentMethodId;
    imageUrl: string;
    name: string;
    type: MkSourceType;
}

export interface MkPlan {
    altName: string;
    bankLinks: MkPaymentMethodObject[];
    creditCards: MkPaymentMethodObject[];
    currency: MkCurrency;
    description: string;
    discount: Discount;
    duration: number;
    id: MkPlanId;
    interval: MkPlanInterval;
    intervalCount: number;
    label: string;
    name: string;
    price: number;
    recurring: boolean;
    statementDescription: string;
}

export type MkPlanId = number;

export type MkPlanInterval =
    'DAY'
    | 'MONTH'
    | 'WEEK'
    | 'YEAR';

export class MkPlanIntervalValues {
    static DAY: MkPlanInterval = 'DAY';
    static MONTH: MkPlanInterval = 'MONTH';
    static WEEK: MkPlanInterval = 'WEEK';
    static YEAR: MkPlanInterval = 'YEAR';
}

export interface MkRefund {
    amountRefunded: number;
    fullRefund: boolean;
    refundedAt: string;
}

export type MkSourceType =
    'BANKLINK'
    | 'CARD'
    | 'CASH'
    | 'OTHER';

export class MkSourceTypeValues {
    static BANKLINK: MkSourceType = 'BANKLINK';
    static CARD: MkSourceType = 'CARD';
    static CASH: MkSourceType = 'CASH';
    static OTHER: MkSourceType = 'OTHER';
}

export interface MkSubscriptionAdminAction$Request {
    action: MkAdminSubscriptionAction;
    customerId: MkCustomerId;
    subscriptionId: MkSubscriptionId;
    userId: UserId;
}

export interface MkSubscriptionAdminAction$Response {
    subscription: MkAdminExtendedSubscription;
}

export interface MkSubscriptionData {
    amount: number;
    cancelAtPeriodEnd: boolean;
    createdAt: number;
    currency: MkCurrency;
    currentPeriodEnd: string;
    currentPeriodStart: string;
    interval: MkPlanInterval;
    intervalCount: number;
    planId: MkPlanId;
    planName: string;
    planStatementDescription: string;
    recurring: boolean;
    status: MkSubscriptionStatus;
    subscriptionId: MkSubscriptionId;
    validUntil: string;
}

export type MkSubscriptionId = number;

export type MkSubscriptionStatus =
    'ACTIVE'
    | 'TRIALING';

export class MkSubscriptionStatusValues {
    static ACTIVE: MkSubscriptionStatus = 'ACTIVE';
    static TRIALING: MkSubscriptionStatus = 'TRIALING';
}

export type MkTransactionId = string;

export interface MkTransactionPayment {
    buyerUserEmail: string;
    coupon: MkCouponId;
    createdAt: number;
    currency: MkCurrency;
    email: string;
    interval: MkPlanInterval;
    intervalCount: number;
    paymentFailed: number;
    periodEnd: string;
    periodStart: string;
    planName: string;
    recipientUserId: UserId;
    recurring: boolean;
    sourceType: MkSourceType;
    status: MkTransactionState;
    subscriptionId: MkSubscriptionId;
    total: number;
    transactionId: MkTransactionId;
}

export interface MkTransactionPaymentRow {
    amountRefunded: number;
    buyerUserEmail: string;
    buyerUserId: UserId;
    couponId: MkCouponId;
    createdAt: number;
    currency: MkCurrency;
    customerId: MkCustomerId;
    email: string;
    fullRefund: boolean;
    id: MkTransactionId;
    paymentsFailed: number;
    periodEnd: string;
    periodStart: string;
    planId: MkPlanId;
    recipientUserEmail: string;
    recipientUserId: UserId;
    refundedAt: number;
    sourceType: MkSourceType;
    status: MkTransactionState;
    subTotal: number;
    subscriptionId: MkSubscriptionId;
    total: number;
}

export type MkTransactionState =
    'APPROVED'
    | 'CANCELLED'
    | 'COMPLETED'
    | 'CREATED'
    | 'EXPIRED'
    | 'PART_REFUNDED'
    | 'PENDING'
    | 'REFUNDED';

export class MkTransactionStateValues {
    static APPROVED: MkTransactionState = 'APPROVED';
    static CANCELLED: MkTransactionState = 'CANCELLED';
    static COMPLETED: MkTransactionState = 'COMPLETED';
    static CREATED: MkTransactionState = 'CREATED';
    static EXPIRED: MkTransactionState = 'EXPIRED';
    static PART_REFUNDED: MkTransactionState = 'PART_REFUNDED';
    static PENDING: MkTransactionState = 'PENDING';
    static REFUNDED: MkTransactionState = 'REFUNDED';
}

export interface MockClock$Request {
    instant: number;
}

export interface MockClock$Response {
}

export interface ModifyFamilyMember$Request {
    profile: Profile;
}

export interface ModifyFamilyMember$Response {
    profile: Profile;
}

export type Month =
    'APRIL'
    | 'AUGUST'
    | 'DECEMBER'
    | 'FEBRUARY'
    | 'JANUARY'
    | 'JULY'
    | 'JUNE'
    | 'MARCH'
    | 'MAY'
    | 'NOVEMBER'
    | 'OCTOBER'
    | 'SEPTEMBER';

export class MonthValues {
    static APRIL: Month = 'APRIL';
    static AUGUST: Month = 'AUGUST';
    static DECEMBER: Month = 'DECEMBER';
    static FEBRUARY: Month = 'FEBRUARY';
    static JANUARY: Month = 'JANUARY';
    static JULY: Month = 'JULY';
    static JUNE: Month = 'JUNE';
    static MARCH: Month = 'MARCH';
    static MAY: Month = 'MAY';
    static NOVEMBER: Month = 'NOVEMBER';
    static OCTOBER: Month = 'OCTOBER';
    static SEPTEMBER: Month = 'SEPTEMBER';
}

export type ObjectType =
    'ALLERGY'
    | 'IAP_PLAN'
    | 'INGREDIENT'
    | 'MK_PLAN'
    | 'PRODUCT'
    | 'PROTIP'
    | 'RECIPE'
    | 'SPECIAL_RECIPE';

export class ObjectTypeValues {
    static ALLERGY: ObjectType = 'ALLERGY';
    static IAP_PLAN: ObjectType = 'IAP_PLAN';
    static INGREDIENT: ObjectType = 'INGREDIENT';
    static MK_PLAN: ObjectType = 'MK_PLAN';
    static PRODUCT: ObjectType = 'PRODUCT';
    static PROTIP: ObjectType = 'PROTIP';
    static RECIPE: ObjectType = 'RECIPE';
    static SPECIAL_RECIPE: ObjectType = 'SPECIAL_RECIPE';
}

export interface OtherItem {
    checked: boolean;
    id: ShoplistOtherItemId;
    name: string;
}

export interface PasswordAuth$Request {
    email: string;
    password: string;
}

export interface PasswordAuth$Response {
    country: Country;
    lang: Lang;
    role: Role;
    userId: UserId;
}

export interface PaymentHistory {
    amount: number;
    cardBrand: string;
    cardLastFour: string;
    currency: string;
    date: string;
    discount: Discount;
    periodEnd: string;
    periodStart: string;
    refund: Refund;
    statementDescriptor: string;
}

export type PaymentProvider =
    'FREE'
    | 'MK'
    | 'STRIPE';

export class PaymentProviderValues {
    static FREE: PaymentProvider = 'FREE';
    static MK: PaymentProvider = 'MK';
    static STRIPE: PaymentProvider = 'STRIPE';
}

export type PlanLabel = string;

export interface PreferencesDto {
    dietStrategy: DietStrategy;
    priceCategories: number[];
    showCarbs: boolean;
    showEatingOut: boolean;
    showPreparedFood: boolean;
    showPriceCategory: boolean;
}

export interface Product {
    amount: number;
    calories: number;
    carbs: number;
    hasPicture: boolean;
    id: ProductId;
    imageBaseUrl: string;
    needHeating: boolean;
    productName: string;
    productStoreId: ProductStoreId;
    proteins: number;
    storeName: string;
}

export type ProductId = number;

export type ProductStoreId = number;

export interface ProductStoreRow {
    id: ProductStoreId;
    storeName: string;
}

export interface Profile {
    age: number;
    allergens: AllergyId[];
    exerciseLevel: ExerciseLevel;
    height: number;
    id: ProfileId;
    idealWeight: number;
    lastName: string;
    lastWeight: WeightEntry;
    name: string;
    sex: ProfileSex;
}

export type ProfileId = number;

export type ProfileSex =
    'FEMALE'
    | 'FEMALE_BREASTFEEDING'
    | 'FEMALE_PREGNANT'
    | 'MALE';

export class ProfileSexValues {
    static FEMALE: ProfileSex = 'FEMALE';
    static FEMALE_BREASTFEEDING: ProfileSex = 'FEMALE_BREASTFEEDING';
    static FEMALE_PREGNANT: ProfileSex = 'FEMALE_PREGNANT';
    static MALE: ProfileSex = 'MALE';
}

export interface Protip {
    content: string;
    header: string;
}

export type ProviderName =
    'FOODMOOD'
    | 'FRESH_GO';

export class ProviderNameValues {
    static FOODMOOD: ProviderName = 'FOODMOOD';
    static FRESH_GO: ProviderName = 'FRESH_GO';
}

export interface PurchaseFreeCampaign$Request {
    campaignId: CampaignId;
    email: string;
    meta: { [key: string ]: any;};
}

export interface PurchaseFreeCampaign$Response {
    status: PurchaseFreeCampaign$Status;
}

export type PurchaseFreeCampaign$Status =
    'ERROR'
    | 'SUCCESS';

export class PurchaseFreeCampaign$StatusValues {
    static ERROR: PurchaseFreeCampaign$Status = 'ERROR';
    static SUCCESS: PurchaseFreeCampaign$Status = 'SUCCESS';
}

export interface PurchaseFreeSubscription$Request {
    couponId: CouponId;
    planLabel: PlanLabel;
}

export interface PurchaseFreeSubscription$Response {
}

export interface PurchaseId {
    id: string;
    type: string;
}

export interface PurchaseMkSubscription$Request {
    couponId: CouponId;
    planLabel: PlanLabel;
}

export interface PurchaseMkSubscription$Response {
    banklinks: MkPaymentMethod[];
    cards: MkPaymentMethod[];
    subscriptionId: SubscriptionId;
    transactionId: MkTransactionId;
}

export interface PurgeImgixImage$Request {
    productId: ProductId;
    recipeId: RecipeId;
}

export interface PurgeImgixImage$Response {
    result: boolean;
}

export interface RateMeal$Request {
    mealId: MealId;
    rating: number;
}

export interface RateMeal$Response {
}

export interface RecalculateFamilyCalories$Request {
}

export interface RecalculateFamilyCalories$Response {
}

export interface RecalculateRecipeAmounts$Request {
    recalculateCalories: boolean;
}

export interface RecalculateRecipeAmounts$Response {
}

export type RecipeId = number;

export interface RecipeSearchItem {
    calories: number;
    carbs: number;
    cookingTime: number;
    imageBaseUrl: string;
    name: string;
    priceCategory: number;
    recipeId: RecipeId;
    recipeTags: RecipeTag[];
}

export interface RecipeSpecialIngredient {
    cookingGuide: string;
    id: SpecialRecipeId;
    name: string;
}

export type RecipeTag =
    'BEEF'
    | 'BOWEL_RELAXING'
    | 'CRAVINGS'
    | 'EATING_OUT'
    | 'MAKE_AHEAD'
    | 'NO_SALT'
    | 'PARTY_FOOD'
    | 'PORK'
    | 'POULTRY'
    | 'PREPARED_FOOD'
    | 'QUICK'
    | 'SEAFOOD'
    | 'SPICY'
    | 'SWEET'
    | 'VEGETARIAN';

export class RecipeTagValues {
    static BEEF: RecipeTag = 'BEEF';
    static BOWEL_RELAXING: RecipeTag = 'BOWEL_RELAXING';
    static CRAVINGS: RecipeTag = 'CRAVINGS';
    static EATING_OUT: RecipeTag = 'EATING_OUT';
    static MAKE_AHEAD: RecipeTag = 'MAKE_AHEAD';
    static NO_SALT: RecipeTag = 'NO_SALT';
    static PARTY_FOOD: RecipeTag = 'PARTY_FOOD';
    static PORK: RecipeTag = 'PORK';
    static POULTRY: RecipeTag = 'POULTRY';
    static PREPARED_FOOD: RecipeTag = 'PREPARED_FOOD';
    static QUICK: RecipeTag = 'QUICK';
    static SEAFOOD: RecipeTag = 'SEAFOOD';
    static SPICY: RecipeTag = 'SPICY';
    static SWEET: RecipeTag = 'SWEET';
    static VEGETARIAN: RecipeTag = 'VEGETARIAN';
}

export interface RedeemGift$Request {
    giftId: MkGiftId;
}

export interface RedeemGift$Response {
}

export interface Refund {
    amountRefunded: number;
    currency: string;
    date: string;
    fullRefund: boolean;
}

export interface RemoveCartItem$Request {
    cartId: CartId;
    items: CartItem[];
}

export interface RemoveCartItem$Response {
    provider: ProviderName;
    shopcartItemList: Shopcart[];
    url: string;
}

export interface RemoveFamilyMember$Request {
    profileId: ProfileId;
}

export interface RemoveFamilyMember$Response {
    status: boolean;
}

export interface RemoveMeal$Request {
    mealId: MealId;
}

export interface RemoveMeal$Response {
}

export interface RemoveOtherItem$Request {
    itemId: ShoplistOtherItemId;
}

export interface RemoveOtherItem$Response {
    shoplist: Shoplist;
}

export interface RemoveProfileFromMeal$Request {
    mealId: MealId;
    profileId: ProfileId;
}

export interface RemoveProfileFromMeal$Response {
}

export interface RemoveShopcart$Request {
    cartId: CartId;
}

export interface RemoveShopcart$Response {
}

export interface RemoveSubscription$Request {
    subscriptionId: StripeSubscriptionId;
}

export interface RemoveSubscription$Response {
}

export interface ReplaceIngredient$Request {
    ingredientId: IngredientId;
    mealId: MealId;
    replacedWith: IngredientId;
}

export interface ReplaceIngredient$Response {
}

export interface ResolveConflicts$Request {
}

export interface ResolveConflicts$Response {
}

export type Role =
    'ADMIN'
    | 'ANONYMOUS'
    | 'NORMAL'
    | 'NO_EMAIL'
    | 'NO_PROFILE';

export class RoleValues {
    static ADMIN: Role = 'ADMIN';
    static ANONYMOUS: Role = 'ANONYMOUS';
    static NORMAL: Role = 'NORMAL';
    static NO_EMAIL: Role = 'NO_EMAIL';
    static NO_PROFILE: Role = 'NO_PROFILE';
}

export interface SaveAdminAllergen$Request {
    allergen: AdminAllergen;
}

export interface SaveAdminAllergen$Response {
    allergen: AdminAllergen;
}

export interface SaveAdminCoupon$Request {
    coupon: MkCouponRow;
}

export interface SaveAdminCoupon$Response {
    coupon: MkCouponRow;
}

export interface SaveAdminIngredient$Request {
    ingredient: AdminFullIngredient;
}

export interface SaveAdminIngredient$Response {
    ingredient: AdminFullIngredient;
}

export interface SaveAdminPlan$Request {
    plan: AdminMkPlan;
}

export interface SaveAdminPlan$Response {
    plan: AdminMkPlan;
}

export interface SaveAdminProduct$Request {
    product: AdminFullProduct;
}

export interface SaveAdminProduct$Response {
    product: AdminFullProduct;
}

export interface SaveAdminRecipe$Request {
    recipe: AdminFullRecipe;
}

export interface SaveAdminRecipe$Response {
    recipe: AdminFullRecipe;
}

export interface SaveAdminSpecialRecipe$Request {
    specialRecipe: AdminFullSpecialRecipe;
}

export interface SaveAdminSpecialRecipe$Response {
    specialRecipe: AdminFullSpecialRecipe;
}

export interface SaveAdminUser$Request {
    user: AdminUserRow;
}

export interface SaveAdminUser$Response {
    user: AdminUserRow;
}

export interface SaveLegalTexts$Request {
    legalTextRow: LegalTextRow;
}

export interface SaveLegalTexts$Response {
    result: boolean;
}

export interface SearchForAllergens$Request {
    text: string;
}

export interface SearchForAllergens$Response {
    ingredients: SearchResult[];
}

export interface SearchForIngredients$Request {
    includeSpecialRecipes: boolean;
    text: string;
}

export interface SearchForIngredients$Response {
    searchResult: AdminRecipeIngredient[];
}

export interface SearchRecipeSwap$Request {
    ingredientIds: IngredientId[];
    mealId: MealId;
    recipeName: string;
    tags: RecipeTag[];
}

export interface SearchRecipeSwap$Response {
    recipes: RecipeSearchItem[];
    summaries: MealSummary[];
}

export interface SearchResult {
    id: number;
    name: string;
    objectType: ObjectType;
}

export interface ShareMeals$Request {
    email: string;
    meals: MealId[];
}

export interface ShareMeals$Response {
    status: boolean;
}

export interface ShareMenu$Request {
    days: LocalDate[];
    email: string;
}

export interface ShareMenu$Response {
}

export interface ShareShoppingList$Request {
    email: string;
}

export interface ShareShoppingList$Response {
}

export interface Shopcart {
    availablesizeAtProvider: number;
    count: number;
    ingredientId: IngredientId;
    ingredientName: string;
    nameAtProvider: string;
    requiredAmount: number;
}

export interface Shoplist {
    active: boolean;
    createdAt: string;
    dates: string[];
    id: ShoplistId;
    otherItems: OtherItem[];
    shoplistCategoryItems: ShoplistCategoryItem[];
}

export type ShoplistCategory =
    'DAIRY_AND_EGGS'
    | 'DRINKS'
    | 'DRY_INGREDIENTS'
    | 'FRUITS_AND_BERRIES'
    | 'MEAT'
    | 'NUTS_SEEDS_DRIED_FRUIT'
    | 'OTHER'
    | 'POULTRY'
    | 'SEAFOOD'
    | 'SEASONING_AND_OIL'
    | 'VEGETABLES';

export class ShoplistCategoryValues {
    static DAIRY_AND_EGGS: ShoplistCategory = 'DAIRY_AND_EGGS';
    static DRINKS: ShoplistCategory = 'DRINKS';
    static DRY_INGREDIENTS: ShoplistCategory = 'DRY_INGREDIENTS';
    static FRUITS_AND_BERRIES: ShoplistCategory = 'FRUITS_AND_BERRIES';
    static MEAT: ShoplistCategory = 'MEAT';
    static NUTS_SEEDS_DRIED_FRUIT: ShoplistCategory = 'NUTS_SEEDS_DRIED_FRUIT';
    static OTHER: ShoplistCategory = 'OTHER';
    static POULTRY: ShoplistCategory = 'POULTRY';
    static SEAFOOD: ShoplistCategory = 'SEAFOOD';
    static SEASONING_AND_OIL: ShoplistCategory = 'SEASONING_AND_OIL';
    static VEGETABLES: ShoplistCategory = 'VEGETABLES';
}

export interface ShoplistCategoryItem {
    shoplistCategory: ShoplistCategory;
    shoplistItems: ShoplistItem[];
}

export type ShoplistId = number;

export interface ShoplistItem {
    checked: boolean;
    ingredient: Ingredient;
    shoplistMealItems: ShoplistMealItem[];
    totalAmount: number;
}

export interface ShoplistMealItem {
    amount: number;
    date: string;
    mealType: MealType;
    recipeId: RecipeId;
    recipeName: string;
    replacedIngredient: Ingredient;
    unit: IngredientUnit;
}

export interface ShoplistOption {
    date: string;
    recipes: ShoplistRecipe[];
}

export type ShoplistOtherItemId = number;

export interface ShoplistProfile {
    checked: boolean;
    firstName: string;
    lastName: string;
    profileId: ProfileId;
}

export interface ShoplistRecipe {
    date: string;
    description: string;
    image: string;
    mealId: MealId;
    mealType: MealType;
    profiles: ShoplistProfile[];
}

export type ShoplistStatus =
    'OK'
    | 'SHOPLIST_DOES_NOT_EXIST';

export class ShoplistStatusValues {
    static OK: ShoplistStatus = 'OK';
    static SHOPLIST_DOES_NOT_EXIST: ShoplistStatus = 'SHOPLIST_DOES_NOT_EXIST';
}

export interface SourceData {
    expirationMonth: number;
    expirationYear: number;
    lastFour: string;
}

export type SpecialRecipeId = number;

export interface StoreProducts {
    productStore: ProductStoreRow;
    products: Product[];
}

export interface StripeCoupon {
    amountOff: number;
    percentageOff: number;
    valid: boolean;
}

export type StripeCustomerId = string;

export type StripePlanId = string;

export type StripePlanInterval =
    'DAY'
    | 'MONTH'
    | 'WEEK'
    | 'YEAR';

export class StripePlanIntervalValues {
    static DAY: StripePlanInterval = 'DAY';
    static MONTH: StripePlanInterval = 'MONTH';
    static WEEK: StripePlanInterval = 'WEEK';
    static YEAR: StripePlanInterval = 'YEAR';
}

export type StripeSubscriptionId = string;

export type StripeSubscriptionStatus =
    'ACTIVE'
    | 'CANCELED'
    | 'PAST_DUE'
    | 'TRIALING'
    | 'UNPAID';

export class StripeSubscriptionStatusValues {
    static ACTIVE: StripeSubscriptionStatus = 'ACTIVE';
    static CANCELED: StripeSubscriptionStatus = 'CANCELED';
    static PAST_DUE: StripeSubscriptionStatus = 'PAST_DUE';
    static TRIALING: StripeSubscriptionStatus = 'TRIALING';
    static UNPAID: StripeSubscriptionStatus = 'UNPAID';
}

export interface SubmitCart$Request {
    cartId: CartId;
}

export interface SubmitCart$Response {
    submittedAt: number;
    url: string;
}

export interface SubscribeCreditCard$Request {
    brand: string;
    expMonth: number;
    expYear: number;
    lastTwo: string;
    paymentToken: string;
    transactionId: MkTransactionId;
}

export interface SubscribeCreditCard$Response {
    status: boolean;
}

export interface SubscribeFreeCoupon$Request {
    couponId: MkCouponId;
    planId: MkPlanId;
}

export interface SubscribeFreeCoupon$Response {
}

export interface SubscribeGiftCreditCard$Request {
    paymentToken: string;
    transactionId: MkTransactionId;
}

export interface SubscribeGiftCreditCard$Response {
    result: boolean;
}

export interface SubscribeGiftFreeCoupon$Request {
    buyerEmail: string;
    couponId: MkCouponId;
    planId: MkPlanId;
    recipientEmail: string;
}

export interface SubscribeGiftFreeCoupon$Response {
}

export interface SubscribeIap$Request {
    receipt: string;
}

export interface SubscribeIap$Response {
    status: SubscribeIap$Status;
    subscriptionId: SubscriptionId;
}

export type SubscribeIap$Status =
    'EMPTY_RECEIPT'
    | 'OK';

export class SubscribeIap$StatusValues {
    static EMPTY_RECEIPT: SubscribeIap$Status = 'EMPTY_RECEIPT';
    static OK: SubscribeIap$Status = 'OK';
}

export interface SubscriptionData {
    amount: number;
    cancelAtPeriodEnd: boolean;
    createdAt: string;
    currency: string;
    currentPeriodEnd: string;
    currentPeriodStart: string;
    interval: StripePlanInterval;
    intervalCount: number;
    metaData: { [key: string ]: string;};
    planId: StripePlanId;
    planName: string;
    planStatementDescription: string;
    recurring: boolean;
    status: StripeSubscriptionStatus;
    subscriptionId: StripeSubscriptionId;
}

export type SubscriptionId = string;

export interface SubscriptionPlan {
    amount: number;
    currency: string;
    id: StripePlanId;
    interval: StripePlanInterval;
    intervalCount: number;
    metaData: { [key: string ]: string;};
    name: string;
    statementDescriptor: string;
}

export interface SwapRecipe$Request {
    mealId: MealId;
    recipeId: RecipeId;
}

export interface SwapRecipe$Response {
    meal: MealSummary;
}

export interface SwitchSubscriptionPlan$Request {
    authRequired: boolean;
    cardToken: string;
    couponId: string;
    planId: StripePlanId;
    subscriptionId: StripeSubscriptionId;
    threeDToken: string;
}

export interface SwitchSubscriptionPlan$Response {
    clientSecret: string;
    redirectUrl: string;
    sourceId: string;
}

export interface ToggleShowRecipeCarbs$Request {
    showRecipeCarbs: boolean;
}

export interface ToggleShowRecipeCarbs$Response {
    isUpdated: boolean;
}

export interface TranslationValue {
    lang: Lang;
    text: string;
}

export interface TriggerScheduledTask$Request {
    task: TriggerScheduledTask$Task;
}

export interface TriggerScheduledTask$Response {
}

export type TriggerScheduledTask$Task =
    'GIFT_EMAILS'
    | 'RECURRING_PAYMENTS';

export class TriggerScheduledTask$TaskValues {
    static GIFT_EMAILS: TriggerScheduledTask$Task = 'GIFT_EMAILS';
    static RECURRING_PAYMENTS: TriggerScheduledTask$Task = 'RECURRING_PAYMENTS';
}

export interface UpdateCard$Request {
    stripeToken: string;
}

export interface UpdateCard$Response {
}

export interface UpdateMealRating$Request {
    mealId: MealId;
    rating: number;
}

export interface UpdateMealRating$Response {
}

export interface UpdateMeasurementSystem$Request {
    measurementSystem: MeasurementSystem;
}

export interface UpdateMeasurementSystem$Response {
}

export interface UpdatePassword$Request {
    password: string;
}

export interface UpdatePassword$Response {
}

export interface UpdatePreferences$Request {
    preferences: PreferencesDto;
}

export interface UpdatePreferences$Response {
}

export interface UpdateProfile$Request {
    profile: Profile;
}

export interface UpdateProfile$Response {
    profile: Profile;
}

export interface UpdateProfileExerciseLevel$Request {
    exerciseLevel: ExerciseLevel;
    profileId: ProfileId;
}

export interface UpdateProfileExerciseLevel$Response {
    profile: Profile;
}

export interface UpdateProfileWeight$Request {
    profileId: ProfileId;
    recalculateCalories: boolean;
    weight: number;
}

export interface UpdateProfileWeight$Response {
    profile: Profile;
}

export interface UpdateUserTrial$Request {
    trialExpires: string;
    userId: UserId;
}

export interface UpdateUserTrial$Response {
}

export type UserId = number;

export interface UserRow {
    activeUntil: string;
    channel: string;
    country: Country;
    createdAt: number;
    email: string;
    emailSubscription: boolean;
    hasSubscription: boolean;
    iapCustomerId: IapCustomerId;
    id: UserId;
    lang: Lang;
    lockedAt: number;
    measurementSystem: MeasurementSystem;
    mkCustomerId: MkCustomerId;
    profileId: ProfileId;
    role: Role;
    showPriceCategory: boolean;
    showRecipeCarbs: boolean;
    stripeCustomerId: StripeCustomerId;
    trialExpires: string;
}

export interface ValidateCoupon$Request {
    couponId: string;
}

export interface ValidateCoupon$Response {
    coupon: StripeCoupon;
}

export interface ValidateMkCoupon$Request {
    couponId: MkCouponId;
    planId: MkPlanId;
}

export interface ValidateMkCoupon$Response {
    amountOff: number;
    newPrice: number;
    percentOff: number;
    valid: boolean;
}

export interface WeightEntry {
    weight: number;
    weightDate: number;
}

export interface WhoAmI$AppConfig {
    currency: Currency;
    paymentProvider: PaymentProvider;
    stripeKey: string;
}

export interface WhoAmI$Request {
}

export interface WhoAmI$Response {
    active: boolean;
    config: WhoAmI$AppConfig;
    country: Country;
    features: Feature[];
    lang: Lang;
    loginMethod: LoginMethod;
    mainProfileId: ProfileId;
    role: Role;
    userId: UserId;
}


import {Injectable} from '@angular/core';
import {ApiClient} from './api-client';
import {Observable} from 'rxjs';

@Injectable()
export class ApiService {

    public user: UserApiService;
    public maksekeskus: MaksekeskusApiService;
    public meal: MealApiService;
    public iap: IapApiService;
    public oauth: OauthApiService;
    public test: TestApiService;
    public product: ProductApiService;
    public kpi: KpiApiService;
    public menu: MenuApiService;
    public stripe: StripeApiService;
    public recipe: RecipeApiService;
    public mk: MkApiService;
    public ingredient: IngredientApiService;
    public campaign: CampaignApiService;
    public legalTexts: LegalTextsApiService;
    public pdfgen: PdfgenApiService;
    public shoplist: ShoplistApiService;
    public admin: AdminApiService;
    public payment: PaymentApiService;
    public weightGraph: WeightGraphApiService;
    public subscription: SubscriptionApiService;
    public family: FamilyApiService;
    public shopcart: ShopcartApiService;
    public allergy: AllergyApiService;
    public entitlement: EntitlementApiService;

    constructor(private api: ApiClient) {
        this.user = new UserApiService(api);
        this.maksekeskus = new MaksekeskusApiService(api);
        this.meal = new MealApiService(api);
        this.iap = new IapApiService(api);
        this.oauth = new OauthApiService(api);
        this.test = new TestApiService(api);
        this.product = new ProductApiService(api);
        this.kpi = new KpiApiService(api);
        this.menu = new MenuApiService(api);
        this.stripe = new StripeApiService(api);
        this.recipe = new RecipeApiService(api);
        this.mk = new MkApiService(api);
        this.ingredient = new IngredientApiService(api);
        this.campaign = new CampaignApiService(api);
        this.legalTexts = new LegalTextsApiService(api);
        this.pdfgen = new PdfgenApiService(api);
        this.shoplist = new ShoplistApiService(api);
        this.admin = new AdminApiService(api);
        this.payment = new PaymentApiService(api);
        this.weightGraph = new WeightGraphApiService(api);
        this.subscription = new SubscriptionApiService(api);
        this.family = new FamilyApiService(api);
        this.shopcart = new ShopcartApiService(api);
        this.allergy = new AllergyApiService(api);
        this.entitlement = new EntitlementApiService(api);
    }

}

export class UserApiService {
    constructor(private api: ApiClient) {}

    changeUserLanguage(request: ChangeUserLanguage$Request): Observable<ChangeUserLanguage$Response> {
        return this.api.execute('changeUserLanguage', request);
    }
    createProfile(request: CreateProfile$Request): Observable<CreateProfile$Response> {
        return this.api.execute('createProfile', request);
    }
    getDashboardNotifications(): Observable<GetDashboardNotifications$Response> {
        return this.api.execute('getDashboardNotifications', {});
    }
    getPreferences(): Observable<GetPreferences$Response> {
        return this.api.execute('getPreferences', {});
    }
    getProfile(): Observable<GetProfile$Response> {
        return this.api.execute('getProfile', {});
    }
    getSubscriptionData(): Observable<GetSubscriptionData$Response> {
        return this.api.execute('getSubscriptionData', {});
    }
    getUserLocalDate(): Observable<GetUserLocalDate$Response> {
        return this.api.execute('getUserLocalDate', {});
    }
    logout(): Observable<Logout$Response> {
        return this.api.execute('logout', {});
    }
    recalculateFamilyCalories(): Observable<RecalculateFamilyCalories$Response> {
        return this.api.execute('recalculateFamilyCalories', {});
    }
    recalculateRecipeAmounts(request: RecalculateRecipeAmounts$Request): Observable<RecalculateRecipeAmounts$Response> {
        return this.api.execute('recalculateRecipeAmounts', request);
    }
    toggleShowRecipeCarbs(request: ToggleShowRecipeCarbs$Request): Observable<ToggleShowRecipeCarbs$Response> {
        return this.api.execute('toggleShowRecipeCarbs', request);
    }
    updateMeasurementSystem(request: UpdateMeasurementSystem$Request): Observable<UpdateMeasurementSystem$Response> {
        return this.api.execute('updateMeasurementSystem', request);
    }
    updatePassword(request: UpdatePassword$Request): Observable<UpdatePassword$Response> {
        return this.api.execute('updatePassword', request);
    }
    updatePreferences(request: UpdatePreferences$Request): Observable<UpdatePreferences$Response> {
        return this.api.execute('updatePreferences', request);
    }
    updateProfile(request: UpdateProfile$Request): Observable<UpdateProfile$Response> {
        return this.api.execute('updateProfile', request);
    }
    updateProfileExerciseLevel(request: UpdateProfileExerciseLevel$Request): Observable<UpdateProfileExerciseLevel$Response> {
        return this.api.execute('updateProfileExerciseLevel', request);
    }
    updateProfileWeight(request: UpdateProfileWeight$Request): Observable<UpdateProfileWeight$Response> {
        return this.api.execute('updateProfileWeight', request);
    }
    whoAmI(): Observable<WhoAmI$Response> {
        return this.api.execute('whoAmI', {});
    }

}

export class MaksekeskusApiService {
    constructor(private api: ApiClient) {}

    cancelSubscription(request: CancelSubscription$Request): Observable<CancelSubscription$Response> {
        return this.api.execute('cancelSubscription', request);
    }
    createGiftTransaction(request: CreateGiftTransaction$Request): Observable<CreateGiftTransaction$Response> {
        return this.api.execute('createGiftTransaction', request);
    }
    createSelfGiftTransactions(request: CreateSelfGiftTransactions$Request): Observable<CreateSelfGiftTransactions$Response> {
        return this.api.execute('createSelfGiftTransactions', request);
    }
    createTransaction(request: CreateTransaction$Request): Observable<CreateTransaction$Response> {
        return this.api.execute('createTransaction', request);
    }
    getMkGiftPlans(request: GetMkGiftPlans$Request): Observable<GetMkGiftPlans$Response> {
        return this.api.execute('getMkGiftPlans', request);
    }
    getMkPaymentHistory(): Observable<GetMkPaymentHistory$Response> {
        return this.api.execute('getMkPaymentHistory', {});
    }
    getMkPlans(request: GetMkPlans$Request): Observable<GetMkPlans$Response> {
        return this.api.execute('getMkPlans', request);
    }
    redeemGift(request: RedeemGift$Request): Observable<RedeemGift$Response> {
        return this.api.execute('redeemGift', request);
    }
    subscribeCreditCard(request: SubscribeCreditCard$Request): Observable<SubscribeCreditCard$Response> {
        return this.api.execute('subscribeCreditCard', request);
    }
    subscribeFreeCoupon(request: SubscribeFreeCoupon$Request): Observable<SubscribeFreeCoupon$Response> {
        return this.api.execute('subscribeFreeCoupon', request);
    }
    subscribeGiftCreditCard(request: SubscribeGiftCreditCard$Request): Observable<SubscribeGiftCreditCard$Response> {
        return this.api.execute('subscribeGiftCreditCard', request);
    }
    subscribeGiftFreeCoupon(request: SubscribeGiftFreeCoupon$Request): Observable<SubscribeGiftFreeCoupon$Response> {
        return this.api.execute('subscribeGiftFreeCoupon', request);
    }
    validateMkCoupon(request: ValidateMkCoupon$Request): Observable<ValidateMkCoupon$Response> {
        return this.api.execute('validateMkCoupon', request);
    }

}

export class MealApiService {
    constructor(private api: ApiClient) {}

    addMeal(request: AddMeal$Request): Observable<AddMeal$Response> {
        return this.api.execute('addMeal', request);
    }
    addProfileToMeal(request: AddProfileToMeal$Request): Observable<AddProfileToMeal$Response> {
        return this.api.execute('addProfileToMeal', request);
    }
    getAveragePrices(request: GetAveragePrices$Request): Observable<GetAveragePrices$Response> {
        return this.api.execute('getAveragePrices', request);
    }
    getAveragePricesPerDay(): Observable<GetAveragePricesPerDay$Response> {
        return this.api.execute('getAveragePricesPerDay', {});
    }
    getMeal(request: GetMeal$Request): Observable<GetMeal$Response> {
        return this.api.execute('getMeal', request);
    }
    getMealProfiles(request: GetMealProfiles$Request): Observable<GetMealProfiles$Response> {
        return this.api.execute('getMealProfiles', request);
    }
    getMealRecipe(request: GetMealRecipe$Request): Observable<GetMealRecipe$Response> {
        return this.api.execute('getMealRecipe', request);
    }
    getRandomRecipes(request: GetRandomRecipes$Request): Observable<GetRandomRecipes$Response> {
        return this.api.execute('getRandomRecipes', request);
    }
    getRecipes(request: GetRecipes$Request): Observable<GetRecipes$Response> {
        return this.api.execute('getRecipes', request);
    }
    getRecipesOld(request: GetRecipesOld$Request): Observable<GetRecipesOld$Response> {
        return this.api.execute('getRecipesOld', request);
    }
    rateMeal(request: RateMeal$Request): Observable<RateMeal$Response> {
        return this.api.execute('rateMeal', request);
    }
    removeMeal(request: RemoveMeal$Request): Observable<RemoveMeal$Response> {
        return this.api.execute('removeMeal', request);
    }
    removeProfileFromMeal(request: RemoveProfileFromMeal$Request): Observable<RemoveProfileFromMeal$Response> {
        return this.api.execute('removeProfileFromMeal', request);
    }
    shareMeals(request: ShareMeals$Request): Observable<ShareMeals$Response> {
        return this.api.execute('shareMeals', request);
    }
    swapRecipe(request: SwapRecipe$Request): Observable<SwapRecipe$Response> {
        return this.api.execute('swapRecipe', request);
    }
    updateMealRating(request: UpdateMealRating$Request): Observable<UpdateMealRating$Response> {
        return this.api.execute('updateMealRating', request);
    }

}

export class IapApiService {
    constructor(private api: ApiClient) {}

    canSubscribeIap(): Observable<CanSubscribeIap$Response> {
        return this.api.execute('canSubscribeIap', {});
    }
    getIapPlans(): Observable<GetIapPlans$Response> {
        return this.api.execute('getIapPlans', {});
    }
    subscribeIap(request: SubscribeIap$Request): Observable<SubscribeIap$Response> {
        return this.api.execute('subscribeIap', request);
    }

}

export class OauthApiService {
    constructor(private api: ApiClient) {}

    passwordAuth(request: PasswordAuth$Request): Observable<PasswordAuth$Response> {
        return this.api.execute('passwordAuth', request);
    }

}

export class TestApiService {
    constructor(private api: ApiClient) {}

    mockClock(request: MockClock$Request): Observable<MockClock$Response> {
        return this.api.execute('mockClock', request);
    }
    triggerScheduledTask(request: TriggerScheduledTask$Request): Observable<TriggerScheduledTask$Response> {
        return this.api.execute('triggerScheduledTask', request);
    }

}

export class ProductApiService {
    constructor(private api: ApiClient) {}

    getProducts(request: GetProducts$Request): Observable<GetProducts$Response> {
        return this.api.execute('getProducts', request);
    }

}

export class KpiApiService {
    constructor(private api: ApiClient) {}

    getKpiDates(): Observable<GetKpiDates$Response> {
        return this.api.execute('getKpiDates', {});
    }

}

export class MenuApiService {
    constructor(private api: ApiClient) {}

    calculateBMI(request: CalculateBMI$Request): Observable<CalculateBMI$Response> {
        return this.api.execute('calculateBMI', request);
    }
    getDailyMenu(request: GetDailyMenu$Request): Observable<GetDailyMenu$Response> {
        return this.api.execute('getDailyMenu', request);
    }
    getWeekMenu(request: GetWeekMenu$Request): Observable<GetWeekMenu$Response> {
        return this.api.execute('getWeekMenu', request);
    }
    resolveConflicts(): Observable<ResolveConflicts$Response> {
        return this.api.execute('resolveConflicts', {});
    }
    shareMenu(request: ShareMenu$Request): Observable<ShareMenu$Response> {
        return this.api.execute('shareMenu', request);
    }

}

export class StripeApiService {
    constructor(private api: ApiClient) {}

    createGiftSubscription(request: CreateGiftSubscription$Request): Observable<CreateGiftSubscription$Response> {
        return this.api.execute('createGiftSubscription', request);
    }
    createSubscription(request: CreateSubscription$Request): Observable<CreateSubscription$Response> {
        return this.api.execute('createSubscription', request);
    }
    getGiftPlans(): Observable<GetGiftPlans$Response> {
        return this.api.execute('getGiftPlans', {});
    }
    getPaymentHistory(): Observable<GetPaymentHistory$Response> {
        return this.api.execute('getPaymentHistory', {});
    }
    getPaymentNotification(): Observable<GetPaymentNotification$Response> {
        return this.api.execute('getPaymentNotification', {});
    }
    getSubscriptionPlans(): Observable<GetSubscriptionPlans$Response> {
        return this.api.execute('getSubscriptionPlans', {});
    }
    removeSubscription(request: RemoveSubscription$Request): Observable<RemoveSubscription$Response> {
        return this.api.execute('removeSubscription', request);
    }
    switchSubscriptionPlan(request: SwitchSubscriptionPlan$Request): Observable<SwitchSubscriptionPlan$Response> {
        return this.api.execute('switchSubscriptionPlan', request);
    }
    updateCard(request: UpdateCard$Request): Observable<UpdateCard$Response> {
        return this.api.execute('updateCard', request);
    }
    validateCoupon(request: ValidateCoupon$Request): Observable<ValidateCoupon$Response> {
        return this.api.execute('validateCoupon', request);
    }

}

export class RecipeApiService {
    constructor(private api: ApiClient) {}

    getAllRecipes(): Observable<GetAllRecipes$Response> {
        return this.api.execute('getAllRecipes', {});
    }
    searchRecipeSwap(request: SearchRecipeSwap$Request): Observable<SearchRecipeSwap$Response> {
        return this.api.execute('searchRecipeSwap', request);
    }

}

export class MkApiService {
    constructor(private api: ApiClient) {}

    getTransactionStatus(request: GetTransactionStatus$Request): Observable<GetTransactionStatus$Response> {
        return this.api.execute('getTransactionStatus', request);
    }
    handleMkReturnMessage(request: HandleMkReturnMessage$Request): Observable<HandleMkReturnMessage$Response> {
        return this.api.execute('handleMkReturnMessage', request);
    }

}

export class IngredientApiService {
    constructor(private api: ApiClient) {}

    getIngredientReplacements(request: GetIngredientReplacements$Request): Observable<GetIngredientReplacements$Response> {
        return this.api.execute('getIngredientReplacements', request);
    }
    replaceIngredient(request: ReplaceIngredient$Request): Observable<ReplaceIngredient$Response> {
        return this.api.execute('replaceIngredient', request);
    }

}

export class CampaignApiService {
    constructor(private api: ApiClient) {}

    getCharityCampaignDetails(request: GetCharityCampaignDetails$Request): Observable<GetCharityCampaignDetails$Response> {
        return this.api.execute('getCharityCampaignDetails', request);
    }
    getCharityCampaignSummary(request: GetCharityCampaignSummary$Request): Observable<GetCharityCampaignSummary$Response> {
        return this.api.execute('getCharityCampaignSummary', request);
    }
    purchaseFreeCampaign(request: PurchaseFreeCampaign$Request): Observable<PurchaseFreeCampaign$Response> {
        return this.api.execute('purchaseFreeCampaign', request);
    }

}

export class LegalTextsApiService {
    constructor(private api: ApiClient) {}

    getLegalTexts(request: GetLegalTexts$Request): Observable<GetLegalTexts$Response> {
        return this.api.execute('getLegalTexts', request);
    }

}

export class PdfgenApiService {
    constructor(private api: ApiClient) {}

    getPdfGenerationToken(): Observable<GetPdfGenerationToken$Response> {
        return this.api.execute('getPdfGenerationToken', {});
    }

}

export class ShoplistApiService {
    constructor(private api: ApiClient) {}

    addOtherItem(request: AddOtherItem$Request): Observable<AddOtherItem$Response> {
        return this.api.execute('addOtherItem', request);
    }
    checkOtherItem(request: CheckOtherItem$Request): Observable<CheckOtherItem$Response> {
        return this.api.execute('checkOtherItem', request);
    }
    checkShoplistIngredient(request: CheckShoplistIngredient$Request): Observable<CheckShoplistIngredient$Response> {
        return this.api.execute('checkShoplistIngredient', request);
    }
    createShoplist(request: CreateShoplist$Request): Observable<CreateShoplist$Response> {
        return this.api.execute('createShoplist', request);
    }
    deactivateShoplist(): Observable<DeactivateShoplist$Response> {
        return this.api.execute('deactivateShoplist', {});
    }
    getShoplist(): Observable<GetShoplist$Response> {
        return this.api.execute('getShoplist', {});
    }
    getShoplistOptions(): Observable<GetShoplistOptions$Response> {
        return this.api.execute('getShoplistOptions', {});
    }
    removeOtherItem(request: RemoveOtherItem$Request): Observable<RemoveOtherItem$Response> {
        return this.api.execute('removeOtherItem', request);
    }
    shareShoppingList(request: ShareShoppingList$Request): Observable<ShareShoppingList$Response> {
        return this.api.execute('shareShoppingList', request);
    }

}

export class AdminApiService {
    constructor(private api: ApiClient) {}

    changeLanguage(request: ChangeLanguage$Request): Observable<ChangeLanguage$Response> {
        return this.api.execute('changeLanguage', request);
    }
    createMkRefund(request: CreateMkRefund$Request): Observable<CreateMkRefund$Response> {
        return this.api.execute('createMkRefund', request);
    }
    generateMenu(request: GenerateMenu$Request): Observable<GenerateMenu$Response> {
        return this.api.execute('generateMenu', request);
    }
    generateUserMagicLink(request: GenerateUserMagicLink$Request): Observable<GenerateUserMagicLink$Response> {
        return this.api.execute('generateUserMagicLink', request);
    }
    getAdminAllergen(request: GetAdminAllergen$Request): Observable<GetAdminAllergen$Response> {
        return this.api.execute('getAdminAllergen', request);
    }
    getAdminAllergenList(): Observable<GetAdminAllergenList$Response> {
        return this.api.execute('getAdminAllergenList', {});
    }
    getAdminAllergens(): Observable<GetAdminAllergens$Response> {
        return this.api.execute('getAdminAllergens', {});
    }
    getAdminCoupon(request: GetAdminCoupon$Request): Observable<GetAdminCoupon$Response> {
        return this.api.execute('getAdminCoupon', request);
    }
    getAdminCoupons(request: GetAdminCoupons$Request): Observable<GetAdminCoupons$Response> {
        return this.api.execute('getAdminCoupons', request);
    }
    getAdminIngredient(request: GetAdminIngredient$Request): Observable<GetAdminIngredient$Response> {
        return this.api.execute('getAdminIngredient', request);
    }
    getAdminIngredients(): Observable<GetAdminIngredients$Response> {
        return this.api.execute('getAdminIngredients', {});
    }
    getAdminLegalTexts(): Observable<GetAdminLegalTexts$Response> {
        return this.api.execute('getAdminLegalTexts', {});
    }
    getAdminMkExtendedSubscription(request: GetAdminMkExtendedSubscription$Request): Observable<GetAdminMkExtendedSubscription$Response> {
        return this.api.execute('getAdminMkExtendedSubscription', request);
    }
    getAdminMkTransactions(request: GetAdminMkTransactions$Request): Observable<GetAdminMkTransactions$Response> {
        return this.api.execute('getAdminMkTransactions', request);
    }
    getAdminPlan(request: GetAdminPlan$Request): Observable<GetAdminPlan$Response> {
        return this.api.execute('getAdminPlan', request);
    }
    getAdminPlans(): Observable<GetAdminPlans$Response> {
        return this.api.execute('getAdminPlans', {});
    }
    getAdminProduct(request: GetAdminProduct$Request): Observable<GetAdminProduct$Response> {
        return this.api.execute('getAdminProduct', request);
    }
    getAdminProducts(): Observable<GetAdminProducts$Response> {
        return this.api.execute('getAdminProducts', {});
    }
    getAdminRecipe(request: GetAdminRecipe$Request): Observable<GetAdminRecipe$Response> {
        return this.api.execute('getAdminRecipe', request);
    }
    getAdminRecipes(): Observable<GetAdminRecipes$Response> {
        return this.api.execute('getAdminRecipes', {});
    }
    getAdminSpecialRecipe(request: GetAdminSpecialRecipe$Request): Observable<GetAdminSpecialRecipe$Response> {
        return this.api.execute('getAdminSpecialRecipe', request);
    }
    getAdminSpecialRecipes(): Observable<GetAdminSpecialRecipes$Response> {
        return this.api.execute('getAdminSpecialRecipes', {});
    }
    getAdminUser(request: GetAdminUser$Request): Observable<GetAdminUser$Response> {
        return this.api.execute('getAdminUser', request);
    }
    getAdminUsers(): Observable<GetAdminUsers$Response> {
        return this.api.execute('getAdminUsers', {});
    }
    getAllUsers(request: GetAllUsers$Request): Observable<GetAllUsers$Response> {
        return this.api.execute('getAllUsers', request);
    }
    getMkCouponUses(request: GetMkCouponUses$Request): Observable<GetMkCouponUses$Response> {
        return this.api.execute('getMkCouponUses', request);
    }
    getMkLastPayments(): Observable<GetMkLastPayments$Response> {
        return this.api.execute('getMkLastPayments', {});
    }
    getMkSubscriptions(request: GetMkSubscriptions$Request): Observable<GetMkSubscriptions$Response> {
        return this.api.execute('getMkSubscriptions', request);
    }
    getProductStores(): Observable<GetProductStores$Response> {
        return this.api.execute('getProductStores', {});
    }
    getReplaceableIngredients(): Observable<GetReplaceableIngredients$Response> {
        return this.api.execute('getReplaceableIngredients', {});
    }
    markTransactionAsPaid(request: MarkTransactionAsPaid$Request): Observable<MarkTransactionAsPaid$Response> {
        return this.api.execute('markTransactionAsPaid', request);
    }
    mkSubscriptionAdminAction(request: MkSubscriptionAdminAction$Request): Observable<MkSubscriptionAdminAction$Response> {
        return this.api.execute('mkSubscriptionAdminAction', request);
    }
    purgeImgixImage(request: PurgeImgixImage$Request): Observable<PurgeImgixImage$Response> {
        return this.api.execute('purgeImgixImage', request);
    }
    saveAdminAllergen(request: SaveAdminAllergen$Request): Observable<SaveAdminAllergen$Response> {
        return this.api.execute('saveAdminAllergen', request);
    }
    saveAdminCoupon(request: SaveAdminCoupon$Request): Observable<SaveAdminCoupon$Response> {
        return this.api.execute('saveAdminCoupon', request);
    }
    saveAdminIngredient(request: SaveAdminIngredient$Request): Observable<SaveAdminIngredient$Response> {
        return this.api.execute('saveAdminIngredient', request);
    }
    saveAdminPlan(request: SaveAdminPlan$Request): Observable<SaveAdminPlan$Response> {
        return this.api.execute('saveAdminPlan', request);
    }
    saveAdminProduct(request: SaveAdminProduct$Request): Observable<SaveAdminProduct$Response> {
        return this.api.execute('saveAdminProduct', request);
    }
    saveAdminRecipe(request: SaveAdminRecipe$Request): Observable<SaveAdminRecipe$Response> {
        return this.api.execute('saveAdminRecipe', request);
    }
    saveAdminSpecialRecipe(request: SaveAdminSpecialRecipe$Request): Observable<SaveAdminSpecialRecipe$Response> {
        return this.api.execute('saveAdminSpecialRecipe', request);
    }
    saveAdminUser(request: SaveAdminUser$Request): Observable<SaveAdminUser$Response> {
        return this.api.execute('saveAdminUser', request);
    }
    saveLegalTexts(request: SaveLegalTexts$Request): Observable<SaveLegalTexts$Response> {
        return this.api.execute('saveLegalTexts', request);
    }
    searchForAllergens(request: SearchForAllergens$Request): Observable<SearchForAllergens$Response> {
        return this.api.execute('searchForAllergens', request);
    }
    searchForIngredients(request: SearchForIngredients$Request): Observable<SearchForIngredients$Response> {
        return this.api.execute('searchForIngredients', request);
    }
    updateUserTrial(request: UpdateUserTrial$Request): Observable<UpdateUserTrial$Response> {
        return this.api.execute('updateUserTrial', request);
    }

}

export class PaymentApiService {
    constructor(private api: ApiClient) {}

    cancelSubscription2(request: CancelSubscription2$Request): Observable<CancelSubscription2$Response> {
        return this.api.execute('cancelSubscription2', request);
    }
    createStripePurchase(request: CreateStripePurchase$Request): Observable<CreateStripePurchase$Response> {
        return this.api.execute('createStripePurchase', request);
    }
    getPaymentStatus(): Observable<GetPaymentStatus$Response> {
        return this.api.execute('getPaymentStatus', {});
    }
    getPlans(request: GetPlans$Request): Observable<GetPlans$Response> {
        return this.api.execute('getPlans', request);
    }

}

export class WeightGraphApiService {
    constructor(private api: ApiClient) {}

    getExtendedChartPoints(request: GetExtendedChartPoints$Request): Observable<GetExtendedChartPoints$Response> {
        return this.api.execute('getExtendedChartPoints', request);
    }
    getThumbnailChartPoints(request: GetThumbnailChartPoints$Request): Observable<GetThumbnailChartPoints$Response> {
        return this.api.execute('getThumbnailChartPoints', request);
    }

}

export class SubscriptionApiService {
    constructor(private api: ApiClient) {}

    addAdminCoupon(request: AddAdminCoupon$Request): Observable<AddAdminCoupon$Response> {
        return this.api.execute('addAdminCoupon', request);
    }
    cancelAdminSubscription(request: CancelAdminSubscription$Request): Observable<CancelAdminSubscription$Response> {
        return this.api.execute('cancelAdminSubscription', request);
    }
    editAdminCoupon(request: EditAdminCoupon$Request): Observable<EditAdminCoupon$Response> {
        return this.api.execute('editAdminCoupon', request);
    }
    getAdminCoupon2(request: GetAdminCoupon2$Request): Observable<GetAdminCoupon2$Response> {
        return this.api.execute('getAdminCoupon2', request);
    }
    getAdminPlanList(): Observable<GetAdminPlanList$Response> {
        return this.api.execute('getAdminPlanList', {});
    }
    getAdminSubscriptionDetails(request: GetAdminSubscriptionDetails$Request): Observable<GetAdminSubscriptionDetails$Response> {
        return this.api.execute('getAdminSubscriptionDetails', request);
    }
    getDiscounts(request: GetDiscounts$Request): Observable<GetDiscounts$Response> {
        return this.api.execute('getDiscounts', request);
    }
    getSubscriptionStatus(): Observable<GetSubscriptionStatus$Response> {
        return this.api.execute('getSubscriptionStatus', {});
    }
    listAdminCoupon(): Observable<ListAdminCoupon$Response> {
        return this.api.execute('listAdminCoupon', {});
    }
    purchaseFreeSubscription(request: PurchaseFreeSubscription$Request): Observable<PurchaseFreeSubscription$Response> {
        return this.api.execute('purchaseFreeSubscription', request);
    }
    purchaseMkSubscription(request: PurchaseMkSubscription$Request): Observable<PurchaseMkSubscription$Response> {
        return this.api.execute('purchaseMkSubscription', request);
    }

}

export class FamilyApiService {
    constructor(private api: ApiClient) {}

    addFamilyMember(request: AddFamilyMember$Request): Observable<AddFamilyMember$Response> {
        return this.api.execute('addFamilyMember', request);
    }
    getFamilyAllergens(): Observable<GetFamilyAllergens$Response> {
        return this.api.execute('getFamilyAllergens', {});
    }
    getFamilyMemberList(): Observable<GetFamilyMemberList$Response> {
        return this.api.execute('getFamilyMemberList', {});
    }
    modifyFamilyMember(request: ModifyFamilyMember$Request): Observable<ModifyFamilyMember$Response> {
        return this.api.execute('modifyFamilyMember', request);
    }
    removeFamilyMember(request: RemoveFamilyMember$Request): Observable<RemoveFamilyMember$Response> {
        return this.api.execute('removeFamilyMember', request);
    }

}

export class ShopcartApiService {
    constructor(private api: ApiClient) {}

    addCartItem(request: AddCartItem$Request): Observable<AddCartItem$Response> {
        return this.api.execute('addCartItem', request);
    }
    createShopcart(request: CreateShopcart$Request): Observable<CreateShopcart$Response> {
        return this.api.execute('createShopcart', request);
    }
    getCartId(request: GetCartId$Request): Observable<GetCartId$Response> {
        return this.api.execute('getCartId', request);
    }
    getShopcart(request: GetShopcart$Request): Observable<GetShopcart$Response> {
        return this.api.execute('getShopcart', request);
    }
    removeCartItem(request: RemoveCartItem$Request): Observable<RemoveCartItem$Response> {
        return this.api.execute('removeCartItem', request);
    }
    removeShopcart(request: RemoveShopcart$Request): Observable<RemoveShopcart$Response> {
        return this.api.execute('removeShopcart', request);
    }
    submitCart(request: SubmitCart$Request): Observable<SubmitCart$Response> {
        return this.api.execute('submitCart', request);
    }

}

export class AllergyApiService {
    constructor(private api: ApiClient) {}

    getAllergens(request: GetAllergens$Request): Observable<GetAllergens$Response> {
        return this.api.execute('getAllergens', request);
    }

}

export class EntitlementApiService {
    constructor(private api: ApiClient) {}

    addEntitlement(request: AddEntitlement$Request): Observable<AddEntitlement$Response> {
        return this.api.execute('addEntitlement', request);
    }

}

